// ═══════════════════════════════════════════════════════════════════════════
// TrueHealthAge — Coaching Protocols Library (v3 — clinically reviewed)
//
// Generated from TrueHealthAge_Improved_Protocols_Draft.csv (clinical/coaching
// audit by external research pass, May 2026). 100 protocols total, schema
// matches the v2 library with two refinements:
//   - daily_micro_wins each meet Keith's 4-point rule: behavior change +
//     physical task + weekly frequency + measurable outcome
//   - weekly_additions stripped of mechanical "Add this one layer:" prefix
//   - One-shot medical/urgent protocols have no weekly_additions (they are
//     not 8-week behavior journeys; they are escalation flags)
//
// Voice: Coach K (Keith Kravitz) — first-person, validates effort over
// outcome, plain language, never shaming. Where the research version skewed
// clinical, the language has been kept as-is for accuracy and softened
// only when the original tone disappeared. The base 3 actions per week
// are CORE/MANDATORY; weekly_additions stack as optional bonuses.
// ═══════════════════════════════════════════════════════════════════════════

export const COACHING_PROTOCOLS = {

  // ─────────────────────────────────────────────────────────────────────────
  //  SLEEP & RECOVERY
  // ─────────────────────────────────────────────────────────────────────────

  SLEEP_QUALITY: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "5 min to set up",
    coach_intro: "Bad sleep is exhausting in a way nothing else is — because everything else gets harder when you're not sleeping. So I'm not going to ask you to overhaul your nights. I'm going to ask you to fix one thing. One. The rest follows.",
    this_week: "Pick a wake-up time and hold it — even on weekends. That's the assignment. Bedtime can stay flexible for now. We're anchoring the morning first because your body builds the whole day off when you wake up.",
    coach_story: "I used to sleep only 5 to 6 hours a night and I was always tired by mid-afternoon. So of course I compensated. I caffeinated to get through the day, and on the weekends I'd take an afternoon nap to try to make up for the lost sleep. And even when I did sleep, I often woke up in the middle of the night and struggled to get back. I knew I needed more sleep, but I realized I was on the wrong schedule. I've always been an early riser — up at 5 AM for the gym — but because I was married, I'd stay up with my wife until 11 PM and then try to wind down. That cycle had to break. I started slowly — in bed by 10 PM, no electronics. No TV, no phone. Those were what kept me up way past when I needed to sleep. After a few months my sleep started getting better. A few months after that I backed it up another 30 minutes. Today I'm in bed by 8:30-9:00, sleeping 8-9 hours, and the difference is astronomical. Cut electronics before bed and figure out your natural rhythm. That's the whole game.",
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Hold your wake time within 30 minutes of target today.",
      "Open blinds as soon as you wake up.",
      "Step outside for 5 minutes within 60 minutes of waking.",
      "Skip the snooze button once; sit up when the alarm goes off.",
      "Keep weekend wake time within 60 minutes of your usual time.",
      "Drink coffee or tea outside for 5 minutes.",
      "Look toward outdoor light, not directly at the sun.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. That's the foundation. This week let's add one thing — try going to bed 30 minutes earlier most nights. Don't force sleep, just go to bed. Reading counts.",
    if_did_partial: "That's a real start. Same goal next week — I want to see consistency before we add anything. Tell me what got in the way. Usually it's not the wake-up time itself, it's the night before.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: easier mornings. Week 2: fewer afternoon crashes. Week 4: you'll start naturally waking up a few minutes before your alarm.",
    avoid: "Trying to fix sleep with willpower at 11 PM. Sleep starts in the morning, not at bedtime.",
    red_flags: "If you snore loudly and wake up exhausted no matter how long you slept, that can be sleep apnea — please see a doctor. Same if insomnia lasts more than 2-3 weeks despite a steady routine.",
    tracking_metric: "Days you held your wake-up time",
    review_days: 7,
  },

  SLEEP_PROTOCOL: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "5-10 min",
    coach_intro: "Sleep isn't a luxury — it's when your body actually does the repair work. I've spent years watching what poor sleep does to people clinically (I sold autonomic-nervous-system testing to doctors for a long time, and chronic sleep loss shows up in the data fast). The good news: small consistent changes restore it surprisingly quickly.",
    this_week: "Two anchors this week: same wake-up time daily, and lights down 30 minutes before bed. That's it. Two rules. Hold them and most of the rest gets easier on its own.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "If it is dark when you wake, turn on bright indoor light until sunrise.",
      "Take a 5-minute walk before starting screens.",
      "Put a sticky note by the coffee maker: 'light first.'",
      "Set your caffeine cutoff at least 8 hours before your sleep window.",
      "Move the last coffee 30 minutes earlier than yesterday.",
      "Swap late caffeine for water or herbal tea.",
      "Track the time of your last caffeine for one week.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold those two. Add one more this week if you want — try to be in bed (not asleep, just in bed) by a specific time three nights this week.",
    if_did_partial: "Halfway. Which of the two was harder — the morning or the evening? Tell me, because the fix is different.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: clearer mornings. Week 4: less reliance on caffeine to get going. Month 2: deeper sleep, fewer middle-of-the-night wakings.",
    avoid: "Going to bed earlier without protecting the morning. Bedtime drifts; wake-up time is the anchor that holds.",
    red_flags: "Loud snoring + daytime sleepiness, gasping awake, or sleeping 9+ hours and still tired — that's a sleep-study conversation with your doctor.",
    tracking_metric: "Days you hit both anchors",
    review_days: 7,
  },

  SLEEP_SCHEDULE: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "0 min — it's a schedule choice",
    coach_intro: "Most sleep problems aren't about the sleep itself — they're about an inconsistent schedule. Your body has a clock, and that clock works best when you give it the same signals every day. Your job this week is to be predictable.",
    this_week: "Pick a wake-up time and a bedtime that are realistic for your life — and hold both within a 30-minute window all week, weekends included. Doesn't have to be perfect. Just predictable.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Use a 10-minute walk for the afternoon dip instead of more caffeine.",
      "Start a 30-minute wind-down before your own bedtime.",
      "Pick one repeatable wind-down cue: reading, stretching, music, or quiet conversation.",
      "Write tomorrow’s top 3 tasks before wind-down starts.",
      "Dim overhead lights during the last hour before bed.",
      "Do the same 5-minute closing routine nightly.",
      "Charge your phone across the room before bed.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "That's the win. Schedule consistency does more for sleep than almost anything else. Hold the line another two weeks — that's when most people start seeing the deeper effects.",
    if_did_partial: "Good. Halfway is real progress here. Where did the slip happen — weekend? A late night that bled into the next morning? Knowing the pattern is half the fix.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: easier to fall asleep. Week 4: waking up before your alarm. Month 2: travel and time changes hit you less hard.",
    avoid: "The 'sleep in to catch up' weekend pattern. It feels good Saturday and wrecks you Monday.",
    red_flags: "If you can't fall asleep within 30 minutes of bedtime even with a steady schedule for 3+ weeks, talk to a doctor. Insomnia has real treatments.",
    tracking_metric: "Days both anchors held",
    review_days: 14,
  },

  SLEEP_EXTENSION: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "+30 min sleep",
    coach_intro: "If you're sleeping under 7 hours, you're running on fumes whether you feel it or not. We're not going to add 2 hours overnight — that won't stick. We're going to add 30 minutes and let your body catch up.",
    this_week: "Move your bedtime 30 minutes earlier. That's the whole assignment. Don't change your wake-up time. Just an earlier bedtime, most nights.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Keep the phone out of bed tonight.",
      "Use night mode and lowest brightness if a screen is unavoidable.",
      "Put a book, not a phone, on the nightstand.",
      "Set a 'screens done' alarm 30 minutes before bed.",
      "Make the room cooler before sleep.",
      "Use earplugs, white noise, or fan if noise is an issue.",
      "Use an eye mask or blackout curtains if light leaks in.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold that for two weeks. Then we can move it earlier another 30 minutes if you're still under 8 hours.",
    if_did_partial: "Halfway. The first few nights are the hardest — you're not tired yet at the new time. Stick with it; your body adjusts in about 7-10 days.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: a little wired at the new bedtime. Week 2: starts feeling natural. Week 4: you're falling asleep almost immediately, and waking up clearer.",
    avoid: "Changing wake-up and bedtime at the same time. Pick one to anchor; let the other adjust around it.",
    red_flags: "If you go to bed earlier consistently for 2+ weeks and still feel exhausted, that's not a duration issue — please see a doctor.",
    tracking_metric: "Bedtime each night",
    review_days: 14,
  },

  BEDTIME_PROTOCOL: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "30 min wind-down",
    coach_intro: "The hour before bed is more important than the hour you get into bed. That last 60 minutes is when your body decides whether it's safe to shut down. Most people are still in 'on' mode at your planned bedtime and then wonder why they can't sleep.",
    this_week: "Build a 30-minute wind-down. Lights low, no work, no screens (or at least night-mode), something low-stimulation. Reading, light stretching, conversation, music — you pick. Consistency matters more than perfection.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Keep work materials out of the bedroom tonight.",
      "Make the bed inviting before evening so sleep feels easier later.",
      "Get into bed within 30 minutes of your planned sleep window.",
      "Do not move wake time later just because bedtime slipped.",
      "Pick a realistic bedtime, not the perfect one.",
      "Set a cutoff alarm for the habit that keeps you up.",
      "If you miss one night, hit the next sleep window without penalty.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "That's the pattern that pays off. Hold it. This week add one upgrade — try the same wind-down activity (same book, same stretches) every night. Repetition is a sleep cue.",
    if_did_partial: "Halfway. What's eating the wind-down — TV, phone, work? Whichever it is, that's the thing to address first.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: still feel keyed up at bedtime. Week 2: starting to actually feel sleepy at the right time. Week 4: falling asleep within minutes.",
    avoid: "Doom-scrolling 'just for a minute' once in bed. There is no such thing as a one-minute scroll.",
    red_flags: "Persistent inability to fall asleep within 30 minutes for 3+ weeks → talk to a doctor.",
    tracking_metric: "Nights you completed the wind-down",
    review_days: 7,
  },

  EARLIER_BEDTIME: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "+30-60 min sleep",
    coach_intro: "If you're consistently going to bed later than you want, the fix isn't willpower — it's removing the things that keep you up. Usually a screen. Sometimes a habit. Almost never a real obligation.",
    this_week: "Move bedtime 30 minutes earlier. Identify the one thing that's been keeping you up past your target — TV, phone, late work — and shut it down at the new bedtime. No exceptions this week.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Add 15 minutes to your sleep opportunity tonight.",
      "If you sleep under 7 hours, move bedtime 15 minutes earlier for three nights.",
      "Protect enough time in bed before adding supplements or gadgets.",
      "Do not add 90 minutes at once; use 15–30 minute steps.",
      "Track actual sleep opportunity, not just sleep quality.",
      "Drink 16 oz of water early in your wake window.",
      "Finish most fluids 2–3 hours before your sleep window.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold the line two more weeks before changing anything else. Most of the benefit comes from consistency, not from going earlier and earlier.",
    if_did_partial: "Halfway. The cutoff alarm is the move — most people skip it because they think they don't need it. They do.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: morning energy lifts. Week 4: less afternoon caffeine craving.",
    avoid: "Trying to add 90 minutes overnight. Your body resists big sudden shifts. 30 minutes works.",
    red_flags: "If you go to bed earlier and still wake up exhausted after 2-3 weeks, get checked for sleep apnea or thyroid issues.",
    tracking_metric: "Nights you hit the new bedtime",
    review_days: 14,
  },

  NO_SCREENS_PROTOCOL: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "0 min — it's a removal",
    coach_intro: "Screens before bed do two things to your sleep: the blue light tells your brain it's still daytime, and the content keeps your brain processing instead of winding down. Both matter. The fix is simple in concept and hard in practice — we're going to make it easy.",
    this_week: "30 minutes before bed: phone goes on the charger in another room. TV is okay only if you're winding down to it (not actively engaging). Aim for zero phone in bed. That's it.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Use small sips only in the last hour before bed.",
      "Move the after-dinner water to mid-afternoon.",
      "Track nighttime bathroom trips without judging them.",
      "Finish large meals at least 2–3 hours before sleep when possible.",
      "If hungry late, choose a small protein-based snack.",
      "Notice whether alcohol worsens middle-of-night waking.",
      "Avoid heavy spicy meals close to bedtime.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. That's the single biggest sleep upgrade most people can make. Hold it. After 2 weeks, see if you can move the cutoff to an hour before bed.",
    if_did_partial: "Halfway. The trick is having a real alternative — a book, music, a notepad. If your hand is going to reach for something, give it a non-screen something.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: you'll reach for the phone in bed and notice it's not there. That's the win — the noticing. Week 4: falling asleep faster.",
    avoid: "'Just checking one thing.' That's how 90 minutes disappears.",
    red_flags: "If you need the phone in bed because of a real anxiety pattern (checking notifications compulsively), that's worth a conversation with a therapist — not just a sleep tweak.",
    tracking_metric: "Nights phone was out of the bedroom",
    review_days: 7,
  },

  HYDRATION_SLEEP: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "0 min — timing change",
    coach_intro: "Most people who wake up at 3 AM to pee are drinking most of their water in the evening. The fix isn't drinking less — it's shifting when. Front-load your hydration in the day and back off in the last 2-3 hours before bed.",
    this_week: "Drink the bulk of your water before 2–3 hours before your sleep window. Last 2-3 hours before bed: small sips only. If you do drink, finish at least an hour before bed.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Track one night of dinner timing and sleep quality.",
      "Keep naps under 20 minutes.",
      "Nap earlier in your wake window, not close to bedtime.",
      "If naps hurt nighttime sleep, skip them for one week.",
      "Use a walk or light exposure before choosing a nap.",
      "Set an alarm before you close your eyes.",
      "If awake at night, keep lights low.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold it. If you're still waking up to pee, the issue may be more than hydration timing — bladder, sleep apnea, or medications.",
    if_did_partial: "Halfway. The trap is the after-dinner glass of water 'just to stay hydrated.' Move that water to mid-afternoon instead.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: still waking once or twice. Week 2-3: fewer middle-of-the-night bathroom trips. Week 4: sleep starts feeling continuous.",
    avoid: "Cutting fluids overall. We're shifting timing, not reducing intake. Daytime hydration matters.",
    red_flags: "Waking up 3+ times a night to urinate, even with fluids cut off, is a doctor conversation — possible diabetes, prostate, or sleep-apnea issue.",
    tracking_metric: "Times you woke to urinate",
    review_days: 14,
  },

  SLEEP_APNEA_CHECK: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "15 min for the appointment call",
    coach_intro: "Sleep apnea is one of the most under-diagnosed conditions out there, and it does real damage over time — to your heart, your brain, your energy. The good news: it's also one of the most treatable. The first step is a conversation with your doctor.",
    this_week: "Call your primary care doctor and ask for a sleep evaluation referral. Tell them you snore loudly, wake up tired, or whatever symptoms apply. That's the assignment.",
    coach_story: null,
    daily_micro_wins: [
      "Write down snoring, gasping, morning headache, or daytime sleepiness symptoms.",
      "Send a doctor portal message or make the sleep-evaluation call.",
      "Use side-sleeping tonight as a short-term bridge, not a replacement for evaluation.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Now show up. The home sleep study is easy — a finger sensor, a nasal cannula, and one night of data. Treatment options have come a long way.",
    if_did_partial: "Tell me what's holding you back — cost, fear, time? Each has a workaround. The condition doesn't get better on its own.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "If treatment is needed, most people feel a difference within the first week of CPAP — like getting their life back.",
    avoid: "Assuming you don't have it because you 'don't snore that bad.' Apnea isn't always loud snoring.",
    red_flags: "Witnessed pauses in breathing, gasping/choking awake, falling asleep at the wheel — that's urgent. Get evaluated this week.",
    tracking_metric: "Appointment scheduled (yes/no)",
    review_days: 7,
  },

  MORNING_LIGHT: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "5-10 min",
    coach_intro: "Most people don't realize how much light controls their day. Your body has a clock, and light is how it knows what time it is. The single fastest way to feel better — better sleep, better energy, better mood — is to give that clock a clear signal in the morning.",
    this_week: "Get outside within 30 minutes of waking up. Even 5 minutes is enough. Doesn't have to be sunny — overcast still works. The trick is no sunglasses and no phone. Just outside.",
    coach_story: "I never thought about morning light and how it affects your body until I listened to Andrew Huberman explain how important it is for setting your circadian rhythm. On the weekends I always felt refreshed when I took my morning cycling ride and got to watch the sun come up — but I never realized how important that was for starting my day right. Now if you're like me, sometimes you're awake well before the sun. That doesn't mean you can't simulate it. Start with a soft light — maybe one in another room — and as you wake up, move to a room where the light is bright. Then as soon as you can, get outside when the sun comes up. Start your day with a little sun and you'll see the difference.",
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Send a doctor portal message if apnea signs are present.",
      "Treat drowsy driving as urgent — pull over and seek help.",
      "Define your personal 'day' and 'night' based on your schedule.",
      "Use bright light at the start of your work shift.",
      "Block light before your sleep window after a night shift.",
      "Protect sleep with eye mask, earplugs, and family boundaries.",
      "Keep caffeine at least 6 hours away from sleep.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "That's the win. Now let's add the other end — try lights low and screens off 30 minutes before bed. The two together work better than either alone.",
    if_did_partial: "Good — you've got the idea. Same goal next week. Tell me what tripped you up. Usually it's just forgetting in the rush of the morning.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: mornings feel less foggy. Week 2: you'll fall asleep a little faster at night. Week 4: you'll start waking up naturally.",
    avoid: "Sunglasses in the morning — they defeat the purpose. And don't doom-scroll in bed; the screen is the wrong kind of light.",
    red_flags: "If you're sleeping 9+ hours and still exhausted, that's not a light issue — please see a doctor.",
    tracking_metric: "Mornings you got outside light",
    review_days: 7,
  },

  MORNING_LIGHT_PROTOCOL: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "5-10 min",
    coach_intro: "Most people don't realize how much light controls their day. Your body has a clock, and light is how it knows what time it is. The single fastest way to feel better — better sleep, better energy, better mood — is to give that clock a clear signal in the morning.",
    this_week: "Get outside within 30 minutes of waking up. Even 5 minutes is enough. Doesn't have to be sunny — overcast still works. The trick is no sunglasses and no phone. Just outside.",
    coach_story: "I never thought about morning light until I listened to Andrew Huberman explain it. On the weekends I always felt refreshed when I took my morning cycling ride and watched the sun come up — but I never connected the dots. Now if you're up before the sun, you can simulate it. Start with a soft light in another room and move to a brighter one as you wake up. Then as soon as the sun comes up, get outside. Start your day with a little sun and you'll see the difference.",
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Anchor to local morning light as soon as practical.",
      "Keep the first meal aligned with your new wake window.",
      "Use a short walk to signal daytime after travel.",
      "Keep the first night simple: dark room, cool room, no phone in bed.",
      "Return to your usual wake anchor the day after travel.",
      "Take one slow morning after a hard week.",
      "Plan a phone-free meal on a recovery day.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "That's the win. This week add the other end — lights low and screens off 30 minutes before bed. The two together compound.",
    if_did_partial: "Good — you've got the idea. Same goal next week. Tell me what tripped you up. Usually it's just forgetting in the rush of the morning.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: mornings feel less foggy. Week 2: you'll fall asleep a little faster. Week 4: waking up naturally.",
    avoid: "Sunglasses in the morning. Doom-scrolling in bed.",
    red_flags: "Sleeping 9+ hours and still exhausted is not a light issue — see a doctor.",
    tracking_metric: "Mornings with outside light",
    review_days: 7,
  },

  CIRCADIAN_RESET: {
    category: 'sleep_recovery',
    difficulty: "moderate",
    time_per_day: "15 min/day for ~2 weeks",
    coach_intro: "When your body clock is fully off — sleeping at 3 AM, waking at 11, can't reset — you need a deliberate reset, not a soft nudge. We're going to use light, food timing, and consistency to drag your clock back to where you want it. It takes about two weeks to take hold.",
    this_week: "Three rules, hold all three: bright light within 30 minutes of waking (outside if possible), no caffeine within 8 hours of your sleep window, and lights low during the final hour before your sleep window. Bedtime moves earlier by 15 minutes per night until you hit your target.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Spend 15 minutes outside without making it exercise.",
      "Skip one optional task to protect rest.",
      "Go to bed early enough to give your body a real chance.",
      "Write one worry on paper and one next action for tomorrow.",
      "Use 5 slow breaths before bed.",
      "Relax jaw and shoulders when you get into bed.",
      "Do not solve work problems in bed.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Strong start. The reset is working. Hold all three for one more week, then your body will lock onto the new schedule.",
    if_did_partial: "Halfway. Which rule is hardest? Usually it's the evening dim — the others depend on it working.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: rough — your body resists. Week 2: starts settling. Week 3: new rhythm feels natural. Don't quit at week 1.",
    avoid: "Trying to reset by 'just going to bed earlier.' Without the light cue and the caffeine cutoff, your brain won't follow.",
    red_flags: "If you've been off-rhythm for months and can't reset despite consistent effort for 3+ weeks, talk to a doctor — could be a circadian rhythm disorder.",
    tracking_metric: "Days all three rules held",
    review_days: 14,
  },

  CIRCADIAN_ALIGNMENT: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "10 min",
    coach_intro: "Your body's clock works best when wake, eat, exercise, and sleep happen at roughly the same time each day. Even a couple of these in alignment matters. We're going to pick the two highest-leverage ones and lock them in.",
    this_week: "Same wake-up time daily, and breakfast (or first meal) within 90 minutes of waking. Don't change anything else. Just those two anchors.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Create a closing phrase: 'Not tonight — tomorrow.'",
      "Move your body during the day, even for 10 minutes.",
      "If intense evening exercise keeps you wired, shift it earlier.",
      "Use gentle stretching at night, not a hard workout.",
      "Take a post-dinner walk instead of late-night scrolling.",
      "Track whether exercise days improve sleep depth.",
      "Stop grading every night as pass/fail.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold them for two more weeks, then add evening anchor — same lights-down time. Three anchors and your clock locks in.",
    if_did_partial: "Halfway. Which was harder — the wake time or the meal? The meal anchor is usually the easier one to slip on, and most useful.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: smoother energy through the day. Week 4: less afternoon slump.",
    avoid: "Skipping breakfast. Even a small protein-based meal within 90 minutes is an anchor signal.",
    red_flags: "If chronic schedule misalignment is unavoidable (shift work), there's a separate protocol for that.",
    tracking_metric: "Days both anchors held",
    review_days: 7,
  },

  CIRCADIAN_LIGHT_THERAPY: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "20-30 min",
    coach_intro: "If you live somewhere with weak winter sun, work indoors all day, or just can't get outside in the morning, a light therapy box can give you the equivalent signal. It's not a gimmick — there's solid clinical evidence behind it for circadian misalignment and seasonal mood issues.",
    this_week: "Get a 10,000 lux light box. Use it for 20-30 minutes within an hour of waking, while you're doing something else (eating, working, reading). Don't stare at it — just have it nearby.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Judge the routine, not one night of sleep.",
      "Repeat the base habits another week before adding more.",
      "Use one small win: wake time, light, or phone away.",
      "If sleep has not improved after 3 weeks of consistency, ask for medical help.",
      "Keep the one sleep habit that helped most.",
      "Remove the sleep habit that added stress but did not help.",
      "Pick a travel version of your routine.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Most people notice the effect within 2 weeks. Hold the routine through the dark months.",
    if_did_partial: "Halfway. The trick is putting the box where you already are in the morning — coffee station, desk, kitchen. Out of sight, out of mind.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: easier mornings. Week 4: better mood through the dark season. Week 6: more consistent sleep timing.",
    avoid: "Using it late in the day. After mid-afternoon and you're pushing your clock the wrong way.",
    red_flags: "Bipolar disorder or eye conditions — talk to your doctor before starting light therapy.",
    tracking_metric: "Days you used the light box",
    review_days: 14,
  },

  CIRCADIAN_SUPPORT: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "5-10 min",
    coach_intro: "Some people don't need a full reset — they just need to support a clock that's slightly off. Small adjustments compound here. We're going to fix the obvious leaks first.",
    this_week: "Three small wins: bright light first thing, dim light during the final hour before your sleep window, and same wake-up time. None of these alone is dramatic. Together they're enough.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Review sleep red flags once a month.",
      "Protect sleep before adding more health goals.",
      "Hold your wake time within 30 minutes of target today.",
      "Open blinds as soon as you wake up.",
      "Step outside for 5 minutes within 60 minutes of waking.",
      "Skip the snooze button once; sit up when the alarm goes off.",
      "Keep weekend wake time within 60 minutes of your usual time.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Hold these — they're the core of a healthy circadian rhythm. Add the other end of the day later (caffeine cutoff, meal timing) when these feel automatic.",
    if_did_partial: "Halfway. Which one is hardest? Most people slip on the evening dim — that's the one to address first.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: clearer mornings. Week 4: more consistent energy through the day.",
    avoid: "Bright overhead lights at 10 PM. They tell your brain it's still daytime.",
    red_flags: "Persistent sleep problems despite a steady rhythm for 3+ weeks → doctor.",
    tracking_metric: "Days all three held",
    review_days: 7,
  },

  SCHEDULE_CONSISTENCY: {
    category: 'sleep_recovery',
    difficulty: "easy",
    time_per_day: "0 min — it's a discipline",
    coach_intro: "Consistency beats intensity for sleep, hands down. The most-disciplined sleeper in the world isn't the one who optimizes everything — it's the one who's predictable. Predictable beats perfect.",
    this_week: "Same wake-up time, same bedtime, same first-meal time — within 30 minutes of target — every day this week. Including Saturday and Sunday.",
    coach_story: null,
    daily_micro_wins: [
      "Hold your wake time within 30 minutes of target.",
      "Get outdoor light or bright indoor light within 60 minutes of waking.",
      "Start a 30-minute wind-down before your own sleep window; phone charges away from bed.",
    ],
    weekly_additions: [
      null,
      "Drink coffee or tea outside for 5 minutes.",
      "Look toward outdoor light, not directly at the sun.",
      "If it is dark when you wake, turn on bright indoor light until sunrise.",
      "Take a 5-minute walk before starting screens.",
      "Put a sticky note by the coffee maker: 'light first.'",
      "Set your caffeine cutoff at least 8 hours before your sleep window.",
      "Move the last coffee 30 minutes earlier than yesterday.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "That's the foundation. Most people see real improvement in 2-3 weeks of this, not from any one tweak.",
    if_did_partial: "Halfway. The weekends usually break the pattern. Monday is hard because Sunday was off-schedule.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: deeper sleep. Week 4: less reliance on caffeine.",
    avoid: "Treating weekends as 'recovery sleep.' That's a myth — the inconsistency does more damage than the catch-up does good.",
    red_flags: "If consistency for 3+ weeks doesn't help, get evaluated — could be sleep apnea, restless legs, or something else.",
    tracking_metric: "Days all windows held",
    review_days: 14,
  },

  SHIFT_PROTOCOL: {
    category: 'sleep_recovery',
    difficulty: "moderate",
    time_per_day: "Schedule-dependent",
    coach_intro: "Shift work is hard on the body — there's no way around that. But there are ways to soften the impact. The goal isn't to pretend you're on a normal schedule. It's to be deliberate about the schedule you actually have.",
    this_week: "Two rules: dark room when sleeping (blackout curtains or eye mask), and bright light when you need to be alert. Treat your wake hours like 'day' and your sleep hours like 'night,' regardless of what time it is on the clock.",
    coach_story: null,
    daily_micro_wins: [
      "Treat your wake window as 'morning' and get bright light early in that window.",
      "Protect your sleep window with darkness, cool temperature, and noise control.",
      "Stop caffeine at least 6 hours before your planned sleep window.",
    ],
    weekly_additions: [
      null,
      "Swap late caffeine for water or herbal tea.",
      "Track the time of your last caffeine for one week.",
      "Use a 10-minute walk for the afternoon dip instead of more caffeine.",
      "Start a 30-minute wind-down before your own bedtime.",
      "Pick one repeatable wind-down cue: reading, stretching, music, or quiet conversation.",
      "Write tomorrow’s top 3 tasks before wind-down starts.",
      "Dim overhead lights during the last hour before bed.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The environment piece is the foundation. Add caffeine timing next — last caffeine 6 hours before your sleep window, no exceptions.",
    if_did_partial: "Halfway. What's leaking — light, noise, family interruptions? Each has a fix.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: deeper sleep during your sleep window. Week 4: more consistent energy during shifts.",
    avoid: "Switching schedules between days off and shift days. The body can't keep up.",
    red_flags: "Persistent fatigue, mood changes, or cognitive issues despite good sleep hygiene — shift work disorder is real and treatable. Talk to a doctor.",
    tracking_metric: "Days you protected your sleep environment",
    review_days: 14,
  },

  SHIFT_ADJUSTMENT: {
    category: 'sleep_recovery',
    difficulty: "moderate",
    time_per_day: "Schedule-dependent",
    coach_intro: "Adjusting to a new shift takes about 7-10 days for your body to fully reset. The first week is the hardest. We're going to help you get through it without sabotaging the long-term adjustment.",
    this_week: "Hit your sleep window every day, even when you don't feel tired. Use bright light at shift start and darkness at shift end. No naps that overlap your future bedtime.",
    coach_story: null,
    daily_micro_wins: [
      "Treat your wake window as 'morning' and get bright light early in that window.",
      "Protect your sleep window with darkness, cool temperature, and noise control.",
      "Stop caffeine at least 6 hours before your planned sleep window.",
    ],
    weekly_additions: [
      null,
      "Do the same 5-minute closing routine nightly.",
      "Charge your phone across the room before bed.",
      "Keep the phone out of bed tonight.",
      "Use night mode and lowest brightness if a screen is unavoidable.",
      "Put a book, not a phone, on the nightstand.",
      "Set a 'screens done' alarm 30 minutes before bed.",
      "Make the room cooler before sleep.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The first week is the worst. By week 2 your body adjusts.",
    if_did_partial: "Halfway. The trap is napping when you're tired but it's not your sleep window. That keeps you out of rhythm.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: rough. Week 2: settling. Week 3: feels normal.",
    avoid: "Long naps. Anything over 20-30 minutes wrecks your main sleep.",
    red_flags: "If a shift change causes severe mood changes or sleep issues lasting more than 3 weeks, see a doctor.",
    tracking_metric: "Sleep window adherence",
    review_days: 14,
  },

  SHIFT_MANAGEMENT: {
    category: 'sleep_recovery',
    difficulty: "moderate",
    time_per_day: "Schedule-dependent",
    coach_intro: "Long-term shift workers need a sustainable system, not just a recovery plan. The goal is: protect sleep, manage caffeine, eat strategically, and accept that your schedule isn't normal — but you can still feel good on it.",
    this_week: "Pick one habit to lock in: protected sleep environment, deliberate caffeine timing, or consistent meal timing relative to your shift. One. We'll add the others as foundations build.",
    coach_story: null,
    daily_micro_wins: [
      "Treat your wake window as 'morning' and get bright light early in that window.",
      "Protect your sleep window with darkness, cool temperature, and noise control.",
      "Stop caffeine at least 6 hours before your planned sleep window.",
    ],
    weekly_additions: [
      null,
      "Use earplugs, white noise, or fan if noise is an issue.",
      "Use an eye mask or blackout curtains if light leaks in.",
      "Keep work materials out of the bedroom tonight.",
      "Make the bed inviting before evening so sleep feels easier later.",
      "Get into bed within 30 minutes of your planned sleep window.",
      "Do not move wake time later just because bedtime slipped.",
      "Pick a realistic bedtime, not the perfect one.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Add the second habit next week. Layered consistency is how shift workers stay healthy long-term.",
    if_did_partial: "Halfway. Which day broke the streak? That's where the system needs reinforcement.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: more energy during shifts. Month 2: less dependence on caffeine.",
    avoid: "Trying to fix everything at once. Layered consistency wins.",
    red_flags: "Chronic exhaustion, increased illness, mood changes — shift work disorder needs medical attention.",
    tracking_metric: "Days the chosen habit held",
    review_days: 7,
  },

  SHIFT_RECOVERY: {
    category: 'sleep_recovery',
    difficulty: "moderate",
    time_per_day: "Off-day focused",
    coach_intro: "Off days are recovery days for shift workers. The temptation is to use them to 'catch up on life' — but that's also when your body is trying to repair from the wear-and-tear of an irregular schedule. Recovery first, life second.",
    this_week: "On at least one off day this week, prioritize: 8+ hours of sleep, 30 minutes outside, one quiet meal, and a real break from screens. Treat it like recovery, not catch-up.",
    coach_story: null,
    daily_micro_wins: [
      "Treat your wake window as 'morning' and get bright light early in that window.",
      "Protect your sleep window with darkness, cool temperature, and noise control.",
      "Stop caffeine at least 6 hours before your planned sleep window.",
    ],
    weekly_additions: [
      null,
      "Set a cutoff alarm for the habit that keeps you up.",
      "If you miss one night, hit the next sleep window without penalty.",
      "Add 15 minutes to your sleep opportunity tonight.",
      "If you sleep under 7 hours, move bedtime 15 minutes earlier for three nights.",
      "Protect enough time in bed before adding supplements or gadgets.",
      "Do not add 90 minutes at once; use 15–30 minute steps.",
      "Track actual sleep opportunity, not just sleep quality.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Hold this every cycle. Recovery days are non-negotiable for sustainable shift work.",
    if_did_partial: "Half a recovery day still counts. What ate the time — chores, family, errands? Some of those are real, some are 'shoulds.'",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: more energy across the cycle. Month 2: fewer 'crash' days.",
    avoid: "Using off days to 'finally exercise' aggressively. That's another stressor on top of shift stress. Gentle movement is fine; intense training waits.",
    red_flags: "If off days don't help you recover — persistent exhaustion across the cycle — it's worth a medical evaluation.",
    tracking_metric: "Real recovery days per cycle",
    review_days: 14,
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  ENERGY & FATIGUE
  // ─────────────────────────────────────────────────────────────────────────

  ENERGY_DIP: {
    category: 'energy_fatigue',
    difficulty: "easy",
    time_per_day: "10 min",
    coach_intro: "An afternoon energy dip is normal. A daily afternoon crash isn't. Most of the time it's blood sugar swinging from a carb-heavy lunch — sometimes it's hydration, sometimes it's circadian. We're going to fix the easy stuff first and see what's left.",
    this_week: "Three things: protein at breakfast, water before coffee in the morning, and a 5-10 minute walk after lunch. That's it. Three small habits, one week.",
    coach_story: null,
    daily_micro_wins: [
      "Drink 16 oz water before or with your first caffeine.",
      "Eat a protein + fiber breakfast or first meal.",
      "Take a 10-minute outdoor walk or three 2-minute movement breaks.",
    ],
    weekly_additions: [
      null,
      "Drink 16 oz water within 30 minutes of waking.",
      "Drink water before coffee.",
      "Keep water visible at your desk.",
      "Add electrolytes only if appropriate for your health and sweat level.",
      "Track urine color once without obsessing.",
      "Eat protein at breakfast or first meal.",
      "Add Greek yogurt, eggs, cottage cheese, tofu, or protein shake once.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold these for two weeks before changing anything else. Energy patterns take time to settle.",
    if_did_partial: "Halfway. Which is hardest? The walk-after-lunch is usually the easiest one to drop, but it's the one with the biggest immediate effect.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: still some afternoon drag. Week 2: notably steadier energy. Week 4: most days you forget about the dip.",
    avoid: "Treating the dip with caffeine or sugar. Both make tomorrow worse.",
    red_flags: "If the dip is severe — falling asleep at your desk, nodding off driving — get checked for sleep apnea, anemia, or thyroid issues.",
    tracking_metric: "Days all three habits held",
    review_days: 7,
  },

  LOW_ENERGY: {
    category: 'energy_fatigue',
    difficulty: "easy",
    time_per_day: "15-20 min",
    coach_intro: "When you're running on low energy most days, the cause is usually one of three: under-sleeping, under-fueling, or chronic stress. We're going to address all three with small, sustainable changes — not by piling on supplements or shortcuts.",
    this_week: "Sleep target: 7+ hours. Hydration: 16 oz of water on waking. Movement: a daily 10-minute walk, no exceptions. That's the trio.",
    coach_story: null,
    daily_micro_wins: [
      "Drink 16 oz water before or with your first caffeine.",
      "Eat a protein + fiber breakfast or first meal.",
      "Take a 10-minute outdoor walk or three 2-minute movement breaks.",
    ],
    weekly_additions: [
      null,
      "Do not let breakfast be only sugar/starch.",
      "Pair fruit with protein.",
      "Notice mid-morning energy after a protein meal.",
      "Pair carbs with protein, fat, or fiber.",
      "Take a 10-minute walk after lunch.",
      "Avoid a carb-only afternoon snack.",
      "Add vegetables or beans to one meal.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good — that's the foundation. Hold it for two weeks before adding anything else.",
    if_did_partial: "Halfway is real. Which leg is weakest — sleep, water, or movement? Address that one specifically.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: energy starts evening out. Week 4: fewer crashes. Month 2: feel like a different person.",
    avoid: "Energy drinks and 'nootropic' shortcuts. They mask the real fix.",
    red_flags: "If you've done the basics for 3-4 weeks and still feel exhausted, get bloodwork done — thyroid, iron, B12, vitamin D, possibly testosterone. Common deficiencies, easy fixes.",
    tracking_metric: "Days all three held",
    review_days: 14,
  },

  SEVERE_FATIGUE: {
    category: 'energy_fatigue',
    difficulty: "moderate",
    time_per_day: "Schedule-dependent",
    coach_intro: "Severe, persistent fatigue isn't just 'low energy' — it's a signal worth taking seriously. We're going to do two things at once: tighten the basics so you're not making it worse, and get you to a doctor for the things only a doctor can rule out.",
    this_week: "Two assignments. First, the basics: 7+ hours of sleep window, three real meals, gentle daily movement. Second, schedule a doctor's visit for bloodwork. Both this week.",
    coach_story: null,
    daily_micro_wins: [
      "Send a doctor portal message or schedule bloodwork this week.",
      "Protect a sleep window and do not add intense training right now.",
      "Eat three real meals or two meals plus one protein/fiber snack.",
    ],
    weekly_additions: [
      null,
      "Track the crash time if it happens.",
      "Set caffeine cutoff at least 8 hours before sleep.",
      "Delay caffeine 30–60 minutes after waking if tolerated.",
      "Swap late caffeine for a walk.",
      "Count cups honestly for one day.",
      "Reduce total caffeine slowly, not cold turkey.",
      "Give yourself a 7-hour sleep opportunity tonight.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The bloodwork is the key piece — most severe fatigue has an identifiable cause once tested. Hold the basics until you have results.",
    if_did_partial: "Halfway. Which got skipped — the basics or the doctor's visit? Tell me, because it changes the next step.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Recovery from severe fatigue often follows a specific cause being addressed. Don't try to power through it.",
    avoid: "Aggressive workouts, intermittent fasting, or restrictive diets while you're severely fatigued. They make it worse.",
    red_flags: "Severe fatigue with weight changes, mood changes, joint pain, or new shortness of breath — see a doctor this week. Several conditions (thyroid, anemia, autoimmune, heart) present this way.",
    tracking_metric: "Energy 1-10 each afternoon + doctor visit scheduled",
    review_days: 7,
    escalation: "Severe fatigue may indicate sleep, nutrition, hormonal, or medical issues. A bloodwork panel is the right first step.",
  },

  BLOOD_SUGAR_SWINGS: {
    category: 'energy_fatigue',
    difficulty: "easy",
    time_per_day: "0 min — it's eating choices",
    coach_intro: "Blood sugar swings feel like an energy problem but they're a meal-timing and meal-composition problem. The fix is well-understood: enough protein, enough fat, enough fiber, eaten on a reasonable schedule. Once you fix this, the daily rollercoaster stops.",
    this_week: "Protein at every meal. 25-30 grams at breakfast specifically. No carb-only snacks. That's the framework.",
    coach_story: null,
    daily_micro_wins: [
      "Drink 16 oz water before or with your first caffeine.",
      "Eat a protein + fiber breakfast or first meal.",
      "Take a 10-minute outdoor walk or three 2-minute movement breaks.",
    ],
    weekly_additions: [
      null,
      "Move bedtime 15 minutes earlier once.",
      "Keep wake time stable even after a rough night.",
      "Keep phone out of bed tonight.",
      "Track sleep hours and energy the next day.",
      "Take three 2-minute movement breaks.",
      "Stand during one call.",
      "Walk 5 minutes when energy drops.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. That's the lever that does the most. This week add the snack rule — no carb-alone snacks (so chips, crackers, candy alone are out; pair them with cheese, nuts, or jerky).",
    if_did_partial: "Halfway. What's breakfast usually look like? If it's a quick carb (toast, cereal, pastry), add an egg or yogurt to it. Don't replace, add.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: still some afternoon dip. Week 2: noticeably steadier mid-day. Week 4: cravings drop.",
    avoid: "'Healthy' high-sugar breakfasts (granola, smoothie bowls, sweetened yogurts). They spike and crash you the same way as donuts.",
    red_flags: "Severe shakiness, lightheadedness, or fainting from low blood sugar — get checked for diabetes or pre-diabetes. Easy bloodwork.",
    tracking_metric: "Days you hit breakfast protein target",
    review_days: 14,
  },

  CAFFEINE_REDUCTION: {
    category: 'energy_fatigue',
    difficulty: "easy",
    time_per_day: "0 min — it's a count",
    coach_intro: "If you're drinking 4+ cups a day to function, the caffeine isn't actually waking you up anymore — it's just preventing the withdrawal. We're going to dial it back gradually so you don't get the headache hit of cold turkey.",
    this_week: "Cut your daily total by 25%. If you're at 4 cups, you're at 3. If you're at 3, you're at 2-and-a-half. Replace one cup with water or herbal tea.",
    coach_story: null,
    daily_micro_wins: [
      "Drink 16 oz water before or with your first caffeine.",
      "Eat a protein + fiber breakfast or first meal.",
      "Take a 10-minute outdoor walk or three 2-minute movement breaks.",
    ],
    weekly_additions: [
      null,
      "Use stairs once if safe.",
      "Set a timer instead of relying on memory.",
      "Step outside for 5 minutes in the morning.",
      "Open blinds immediately.",
      "Take coffee near outdoor light.",
      "Use bright indoor light if sunrise is later.",
      "Do not start the day in a dark room if you can avoid it.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold the new level for two weeks. Then we cut another 25% if you want to keep going.",
    if_did_partial: "Halfway. The trick is preparation — have the herbal tea or water ready, in your usual cup. The hand-to-mouth habit is half the addiction.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: a little headachy day 2-3, then it eases. Week 2: sleep gets noticeably better. Week 4: you'll be drinking less and feeling better.",
    avoid: "Cold turkey. The headaches and fog drive you back to your old level by day 4.",
    red_flags: "If you need 4+ cups despite enough sleep, get bloodwork (thyroid, iron). The reliance often signals an underlying issue.",
    tracking_metric: "Cups per day",
    review_days: 14,
  },

  CAFFEINE_TIMING: {
    category: 'energy_fatigue',
    difficulty: "easy",
    time_per_day: "0 min — it's a rule",
    coach_intro: "I'm not going to ask you to give up coffee or tea. I drink coffee and tea sometimes and even the occasional energy drink. The trick isn't how much, it's when. Late caffeine is the silent killer of good sleep, and bad sleep is the silent killer of everything else.",
    this_week: "No caffeine within 8 hours of your sleep window. That's the whole assignment. One rule, one week.",
    coach_story: "Like most people, I'd start to drag in the afternoon — especially after lunch. Around 2-3 PM the sleepiness would set in, I'd start yawning, and I'd have trouble focusing. So I caffeinated to push through it. And I started to notice a pattern: whenever I caffeinated late in the afternoon, I had two problems. First, I couldn't shut my brain off at bedtime — it just felt super-charged. Second, even when I did fall asleep, two or three hours later my brain would go back into overload and I'd be wide awake. I realized it had to be the caffeine — even hours later, the lingering effects were still there. Once I moved my caffeine to before noon, everything changed. If you're tired in the afternoon, stand up, move around, or take a 10-20 minute nap. Nothing fancy — even putting your head on your desk and closing your eyes works. You'll get the same refreshment without paying for it that night.",
    daily_micro_wins: [
      "Drink 16 oz water before or with your first caffeine.",
      "Eat a protein + fiber breakfast or first meal.",
      "Take a 10-minute outdoor walk or three 2-minute movement breaks.",
    ],
    weekly_additions: [
      null,
      "Drink water before treating the dip with caffeine.",
      "Walk 5–10 minutes after lunch.",
      "Check whether lunch was mostly refined carbs.",
      "Try a protein/fiber snack if truly hungry.",
      "Take a 10–20 minute nap only if it does not hurt sleep.",
      "Eat three real meals or two meals plus one planned snack.",
      "Do not skip meals on severe fatigue days.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Now hold that line for two more weeks. Then watch your sleep — most people are surprised how much falls into place once that one habit sticks.",
    if_did_partial: "Halfway. What's making the afternoon cup hard? Usually it's a 3 PM slump, and that has its own fix — a walk, water, or a real meal hours earlier so blood sugar isn't crashing.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Falling asleep gets easier within 4-5 days. The 'wired but tired' bedtime feeling fades. Mornings get less rough because your sleep is actually restorative.",
    avoid: "Cold turkey. That gives you headaches and makes you give up by Wednesday. We're moving the timing, not cutting the amount.",
    red_flags: "If you need 4+ cups to function in a normal day, there may be a sleep, thyroid, or iron issue underneath the caffeine reliance — worth a checkup.",
    tracking_metric: "Days you stopped by 2 PM",
    review_days: 7,
  },

  SEDENTARY_PATTERN: {
    category: 'energy_fatigue',
    difficulty: "easy",
    time_per_day: "5-20 min",
    coach_intro: "Sitting all day creeps up on you — you don't even notice it happening. Most people don't. So we're not going to overhaul your schedule. We're going to find one moment in your day where you can stand up and take a few steps.",
    this_week: "Take a walk after one meal a day. Doesn't matter which meal. Doesn't have to be long — even just around the block. The goal is starting. Most people struggle with the starting part. Once you start, you usually go far enough.",
    coach_story: "I remember when I first started walking after dinner. I was so full I felt like I was going to fall over. I almost didn't go. But after about 10 minutes of walking, I noticed something — my stomach didn't feel as bad. Turns out walking actually helps your digestion and burns off some of the fuel you just gave your body. So if you're sitting on the couch feeling stuffed, that's actually the best moment to go. Trust me on that one.",
    daily_micro_wins: [
      "Drink 16 oz water before or with your first caffeine.",
      "Eat a protein + fiber breakfast or first meal.",
      "Take a 10-minute outdoor walk or three 2-minute movement breaks.",
    ],
    weekly_additions: [
      null,
      "Plan tomorrow’s first meal tonight.",
      "Keep an emergency protein option available.",
      "Track whether long gaps worsen energy.",
      "Schedule bloodwork if fatigue lasts 3–4 weeks despite basics.",
      "Ask about thyroid, anemia/iron, B12, vitamin D, sleep apnea, depression, and medication effects.",
      "Escalate fatigue with chest pain or shortness of breath.",
      "Do not push intense workouts during severe fatigue.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. That's the hard part — you started. This week, see if you can stretch one of those walks to 20 minutes. Twenty minutes is enough to cover the movement you need for that day.",
    if_did_partial: "That's a start, and a start is what we needed. Same goal this week. If something got in the way, tell me what — sometimes the obstacle isn't the walk, it's the timing.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "By week two, most people feel their afternoon energy lift. By week four, walking starts to feel like part of your day, not a chore.",
    avoid: "Don't try to walk an hour on day one. Don't count steps obsessively. Just start.",
    red_flags: "If walking causes chest pain, severe shortness of breath, or dizziness, stop and talk to your doctor before going further.",
    tracking_metric: "Days you walked after a meal",
    review_days: 7,
  },

  DAILY_MOVEMENT: {
    category: 'energy_fatigue',
    difficulty: "easy",
    time_per_day: "10-15 min",
    coach_intro: "You don't need a gym membership or an exercise plan to undo a sedentary day. You need movement spread through your day — small bouts that keep your body from forgetting how to move. That's it.",
    this_week: "Three movement breaks per day. Each one is just 2-3 minutes. Stand up, walk around, stretch, climb a flight of stairs — anything that's not sitting. Three a day, every day.",
    coach_story: null,
    daily_micro_wins: [
      "Drink 16 oz water before or with your first caffeine.",
      "Eat a protein + fiber breakfast or first meal.",
      "Take a 10-minute outdoor walk or three 2-minute movement breaks.",
    ],
    weekly_additions: [
      null,
      "Write symptom patterns before the appointment.",
      "Use gentle walking, not intense training, when depleted.",
      "Stop before you crash.",
      "Schedule one true rest block.",
      "Rate energy morning and afternoon.",
      "Repeat a manageable dose instead of chasing a comeback.",
      "Identify one energy drain you can remove.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "That's the rhythm. Hold it. This week try to make one of those breaks a 10-minute walk instead of just standing.",
    if_did_partial: "Halfway. The trap is depending on memory. Set three timer alarms on your phone — that's the system.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: less stiffness at the end of the day. Week 4: better focus during the work day too — movement isn't just physical.",
    avoid: "Telling yourself 'I'll just exercise later.' The point is breaks throughout — not one big workout that justifies the rest of the day's stillness.",
    red_flags: "Severe joint pain or new musculoskeletal issues — get evaluated.",
    tracking_metric: "Days with 3+ movement breaks",
    review_days: 7,
  },

  START_MOVING: {
    category: 'energy_fatigue',
    difficulty: "easy",
    time_per_day: "5-10 min",
    coach_intro: "If you've been mostly sedentary for a while, the goal isn't fitness yet — it's simply moving again. We're going to start so small that there's no excuse to skip it. Then we let momentum do the rest.",
    this_week: "Five-minute walks. Three of them this week. Anywhere. Any pace. That's it.",
    coach_story: null,
    daily_micro_wins: [
      "Drink 16 oz water before or with your first caffeine.",
      "Eat a protein + fiber breakfast or first meal.",
      "Take a 10-minute outdoor walk or three 2-minute movement breaks.",
    ],
    weekly_additions: [
      null,
      "Take a phone-free 10-minute break.",
      "Practice 2 minutes of slow breathing.",
      "Set one boundary around after-hours work.",
      "Ask for help before exhaustion becomes normal.",
      "Add one colorful fruit or vegetable.",
      "Choose iron-rich or protein-rich food once if appropriate.",
      "Add omega-3-rich fish or walnuts/chia/flax once this week.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good — you started. That's the whole game. This week, see if you can stretch one of those walks to 10 minutes. The 5-minute baseline stays.",
    if_did_partial: "Even one walk is a start. Same goal this week — we're not adding anything, we're just hitting the count.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: it'll feel weirdly easier than you expected. Week 4: you'll start wanting to walk longer naturally.",
    avoid: "Setting big goals that feel ambitious but produce nothing. A walk you actually take beats a workout plan you don't.",
    red_flags: "Chest pain, severe shortness of breath, or dizziness during walks — stop and see a doctor before continuing.",
    tracking_metric: "5-minute walks per week",
    review_days: 7,
  },

  GENTLE_MOVEMENT: {
    category: 'energy_fatigue',
    difficulty: "easy",
    time_per_day: "5-15 min",
    coach_intro: "If you've got pain, recent illness, or limitations that make 'just walk more' bad advice, we go gentler. Movement is medicine — but the dose matters. We're starting with the smallest dose that still counts.",
    this_week: "Pick movement that doesn't make pain worse. Could be a 5-minute slow walk, gentle stretching, chair-based exercises, or pool walking if available. Daily. Short.",
    coach_story: null,
    daily_micro_wins: [
      "Drink 16 oz water before or with your first caffeine.",
      "Eat a protein + fiber breakfast or first meal.",
      "Take a 10-minute outdoor walk or three 2-minute movement breaks.",
    ],
    weekly_additions: [
      null,
      "Do not start many supplements without labs or clinician input.",
      "Use food quality before chasing stimulants.",
      "Notice sleep quality after alcohol.",
      "Choose one alcohol-free night.",
      "Stop alcohol several hours before sleep if you drink.",
      "Replace a drink with sparkling water once.",
      "Track next-day energy after an alcohol-free night.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The consistency matters more than the duration. This week try to add 2-3 minutes to the daily session.",
    if_did_partial: "Halfway is real progress here. What's limiting you — pain, fatigue, fear of making things worse? Each has different next steps.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: stiffness eases. Week 4: capacity slowly builds. Don't rush this.",
    avoid: "Pushing through pain. Movement should challenge, not damage.",
    red_flags: "Sharp new pain, swelling, or symptoms that worsen with movement → see a doctor or PT before continuing.",
    tracking_metric: "Days with gentle movement",
    review_days: 7,
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  HEART HEALTH & FITNESS
  // ─────────────────────────────────────────────────────────────────────────

  STEPS_PROTOCOL: {
    category: 'heart_fitness',
    difficulty: "easy",
    time_per_day: "20-40 min",
    coach_intro: "Daily steps are one of the most underrated health metrics there is. Not because the number itself is magic — but because it's a proxy for how much you move overall. We're going to nudge yours up without making your day harder.",
    this_week: "Add 1,000 steps to your current daily average. If you don't track, take one extra 10-minute walk per day. That's the equivalent.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 10-minute brisk walk or add 1,000 steps to your baseline.",
      "Keep effort at a conversational pace unless already conditioned.",
      "Stop and seek care for chest pain, dizziness, fainting, or unusual shortness of breath.",
    ],
    weekly_additions: [
      null,
      "Take one 10-minute walk at conversational pace.",
      "Add 1,000 steps to your usual baseline.",
      "Walk after lunch or dinner once.",
      "Use a timer, not motivation, to start.",
      "Log minutes walked, not calories burned.",
      "Add 5 minutes to one walk.",
      "Hit 30 minutes total movement today in any pieces.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold the new baseline two weeks. Then we add another 1,000 if you want to keep building.",
    if_did_partial: "Halfway. Walks tend to be either part of your day (anchored to lunch, work, etc.) or they don't happen. Pick an anchor.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: more energy in the afternoons. Week 4: you'll naturally want to move more.",
    avoid: "Step-count obsession. The 10,000-step number isn't magic — consistency is.",
    red_flags: "New joint pain that lasts more than a few days — get it checked. Stop and seek medical advice for chest pain, fainting, severe dizziness, or unusual shortness of breath.",
    tracking_metric: "Daily steps or extra walks",
    review_days: 14,
  },

  ADD_STEPS: {
    category: 'heart_fitness',
    difficulty: "easy",
    time_per_day: "+10 min/day",
    coach_intro: "You're already moving — that's the hard part, and you're past it. Now we're going to nudge a little more in without making your day harder.",
    this_week: "Add ONE walk this week that you weren't already doing. Pick the spot in your day where it'll be easiest — usually after lunch or right after work.",
    coach_story: "I was never a big walker until I realized how tired I was after every meal. I was listening to Dr. Peter Attia's podcast, and he and his guest were talking about blood sugar and the huge benefits of walking right after a meal. So I tried it — and boy, was I surprised how much it helped. Not just digestion, but my energy and even my sleep. There was a bonus too: my wife and I started walking together after dinner, and we'd talk. The walks went longer without much effort because we were enjoying the time. I highly recommend trying after-meal walks. They're a big win in multiple categories at once.",
    daily_micro_wins: [
      "Do one 10-minute brisk walk or add 1,000 steps to your baseline.",
      "Keep effort at a conversational pace unless already conditioned.",
      "Stop and seek care for chest pain, dizziness, fainting, or unusual shortness of breath.",
    ],
    weekly_additions: [
      null,
      "Plan 5 days with 20–30 minutes of moderate movement.",
      "Use the talk test: you can speak but not sing.",
      "Repeat the same dose for a week before increasing.",
      "Do 2 sets of sit-to-stands.",
      "Do wall push-ups or counter push-ups.",
      "Practice a hip hinge with no weight.",
      "Do calf raises while brushing teeth.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "That's a habit forming. Same anchor next week, but try adding 5 minutes to that walk. You won't notice the difference in the moment, but you'll notice it by month's end.",
    if_did_partial: "Halfway. Pick the easiest spot in your day to wedge it in — usually right after lunch. Put it on the calendar tomorrow with a name: 'lunch walk.'",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Steady afternoon energy by week 2. Less fidgety, fewer 'I need a snack' moments by week 3. Sleeping a little deeper by week 4.",
    avoid: "Tracker obsession. The number of steps doesn't matter as much as showing up. Don't quit because you didn't hit 10,000.",
    red_flags: "New joint pain that doesn't ease up after a few days of rest — get it checked. Stop and seek medical advice for chest pain, fainting, severe dizziness, or unusual shortness of breath.",
    tracking_metric: "Days with the extra walk",
    review_days: 7,
  },

  INCREASE_STEPS: {
    category: 'heart_fitness',
    difficulty: "easy",
    time_per_day: "+15-20 min/day",
    coach_intro: "If you're already moving but stuck at a plateau — same step count for months — we're going to nudge it up gradually. The body adapts to whatever you do consistently. The fix is to slowly raise the floor.",
    this_week: "Add 2,000 steps to your daily average. If you usually do 6,000, aim for 8,000. Most people get there with one extra 20-minute walk.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 10-minute brisk walk or add 1,000 steps to your baseline.",
      "Keep effort at a conversational pace unless already conditioned.",
      "Stop and seek care for chest pain, dizziness, fainting, or unusual shortness of breath.",
    ],
    weekly_additions: [
      null,
      "Schedule two short strength days this week.",
      "Stand on one foot near support for 20 seconds.",
      "Stretch calves or hips for 2 minutes.",
      "Do 5 controlled step-ups if safe.",
      "Practice getting up from a chair without using hands.",
      "Add mobility after walks, not as a separate project.",
      "Measure BP seated with feet flat if you have a cuff.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold it for two weeks before pushing further. Adaptation takes time.",
    if_did_partial: "Halfway. The 20-minute walk is the lever. When can you fit it in non-negotiably — same time every day?",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: cardio fitness improving. Month 2: stairs feel easier.",
    avoid: "Pushing the increase too fast. 2,000 a week is the right pace; 5,000 jumps don't stick.",
    red_flags: "New cardiovascular symptoms — chest pain, shortness of breath out of proportion to effort — see a doctor.",
    tracking_metric: "Daily steps",
    review_days: 14,
  },

  START_WALKING: {
    category: 'heart_fitness',
    difficulty: "easy",
    time_per_day: "10-15 min",
    coach_intro: "Walking is the most under-used exercise there is. It's free, it's effective, it's hard to overdo, and it's the one thing most people can stick with. We're starting here on purpose.",
    this_week: "Three walks. 10-15 minutes each. Anywhere, any pace. The only goal is consistency.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 10-minute brisk walk or add 1,000 steps to your baseline.",
      "Keep effort at a conversational pace unless already conditioned.",
      "Stop and seek care for chest pain, dizziness, fainting, or unusual shortness of breath.",
    ],
    weekly_additions: [
      null,
      "Take two readings one minute apart.",
      "Do not diagnose from one high reading; record and follow up.",
      "Bring your cuff or readings to your clinician.",
      "Know that 180/120 with symptoms is emergency territory.",
      "Choose one lower-sodium swap today.",
      "Add one potassium-rich food if appropriate: fruit, beans, greens, yogurt, or potato.",
      "Read sodium on one packaged food label.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Same goal next week — three walks. Once it's automatic, we'll lengthen one of them.",
    if_did_partial: "Even one walk counts as a start. Same goal next week. What's the obstacle — time, weather, motivation?",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: starts feeling automatic. Week 4: missing it feels weird.",
    avoid: "Buying walking gear before you've earned the habit. Shoes you already have are fine.",
    red_flags: "Chest pain, severe shortness of breath, or dizziness during walks — stop and see a doctor.",
    tracking_metric: "Walks per week",
    review_days: 7,
  },

  CARDIO_BUILDING: {
    category: 'heart_fitness',
    difficulty: "moderate",
    time_per_day: "20-30 min",
    coach_intro: "Building cardio fitness is one of the highest-leverage things you can do for long-term health — heart, brain, mood, longevity. The key is to build slowly so you don't burn out. We're aiming for sustainable, not intense.",
    this_week: "Three 20-minute cardio sessions this week. Walking briskly counts. Cycling, swimming, jogging — your choice. Aim for a pace where you can talk but not sing.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 10-minute brisk walk or add 1,000 steps to your baseline.",
      "Keep effort at a conversational pace unless already conditioned.",
      "Stop and seek care for chest pain, dizziness, fainting, or unusual shortness of breath.",
    ],
    weekly_additions: [
      null,
      "Use herbs/spices before adding salt.",
      "Ask your clinician about potassium if you have kidney disease or take BP meds.",
      "Increase weekly cardio minutes by no more than about 10–20%.",
      "Keep most cardio easy enough to repeat tomorrow.",
      "Add one hill or faster minute only if your base is stable.",
      "Track RPE 1–10 after walking.",
      "Use recovery days as part of training.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold three sessions per week for two weeks. Then add 5 minutes to one of them.",
    if_did_partial: "Halfway. Two sessions is more than zero. Same goal next week — we're building consistency before duration.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: cardiovascular fitness improving — easier breathing on stairs, faster recovery from effort. Month 2: noticeably better.",
    avoid: "Going too hard too fast. Sustainable beats heroic.",
    red_flags: "Chest pain, severe shortness of breath disproportionate to effort, or fainting — stop and see a doctor.",
    tracking_metric: "Sessions per week",
    review_days: 14,
  },

  GRADUAL_CARDIO: {
    category: 'heart_fitness',
    difficulty: "moderate",
    time_per_day: "15-25 min",
    coach_intro: "Cardio that lasts is cardio that's gradual. The people who jump in hard at week 1 are the ones quitting at week 3. We're going to build slowly so the habit holds.",
    this_week: "Two 15-minute cardio sessions, plus one walk. Talk-test pace. That's it. Modest on purpose.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 10-minute brisk walk or add 1,000 steps to your baseline.",
      "Keep effort at a conversational pace unless already conditioned.",
      "Stop and seek care for chest pain, dizziness, fainting, or unusual shortness of breath.",
    ],
    weekly_additions: [
      null,
      "Add 3 x 30-second brisk intervals during a walk if already comfortable.",
      "Recover fully between brisk efforts.",
      "Do not add intervals if walking already feels hard.",
      "Keep intervals once weekly at first.",
      "Stop intervals for chest pain, dizziness, or unusual breathlessness.",
      "Stand up once every hour.",
      "Walk for 2 minutes after long sitting.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold this for two weeks. Then we step up to three sessions.",
    if_did_partial: "Halfway. The trap is missing one session and trying to make it up the next day. Just hit the next planned session.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: easier to climb stairs without huffing. Month 2: notably better stamina.",
    avoid: "Adding more before the current load is automatic.",
    red_flags: "Cardiovascular symptoms beyond normal exertion — see a doctor. Stop and seek medical advice for chest pain, fainting, severe dizziness, or unusual shortness of breath.",
    tracking_metric: "Sessions completed",
    review_days: 14,
  },

  START_CARDIO: {
    category: 'heart_fitness',
    difficulty: "easy",
    time_per_day: "10-20 min",
    coach_intro: "You're not behind. You're starting. And starting is the hard part — most people who 'should be exercising' are stuck at zero, just like you. We're going to move you from zero to one, and that's the only number that actually matters.",
    this_week: "Walk 3 days this week. Anywhere from 10 to 20 minutes. That's it. No specific pace, no specific route, no app required.",
    coach_story: "I'm not going to lie to you — I've been working out most of my life. I started when I was 14, so exercise has always been a habit. But here's the thing: as a coach of athletes, I can tell you even the best athletes sometimes aren't motivated to train. They say 'I'm tired' or 'I have to do something else' or 'I'm just not feeling it today.' I've had those thoughts myself. Then I remembered something a trainer at the gym told me: *Just show up.* That's the hardest part. Once you're in the gym — or once you start walking — one exercise turns into two. A couple of steps turns into half a block. Half a block turns into a full block, then maybe a mile or two. The human mind creates obstacles, but once you get past the first one, the rest gets easier. So just start. Before you know it, you'll be doing it regularly and your health will be improving.",
    daily_micro_wins: [
      "Do one 10-minute brisk walk or add 1,000 steps to your baseline.",
      "Keep effort at a conversational pace unless already conditioned.",
      "Stop and seek care for chest pain, dizziness, fainting, or unusual shortness of breath.",
    ],
    weekly_additions: [
      null,
      "Take one call standing.",
      "Use stairs once if safe.",
      "Put water farther away so you have to get up.",
      "Add vegetables to one meal.",
      "Choose fish, beans, poultry, tofu, or lean protein once.",
      "Use olive oil, nuts, or avocado instead of fried foods once.",
      "Choose whole grain over refined grain once.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. That's the win. Now I'm going to challenge you — see if you can stretch one of those walks to 20 minutes this week. Twenty minutes is enough to count as your day's exercise. You don't need anything more dramatic than that.",
    if_did_partial: "That's progress. Same goal next week — I want to see three before we add anything. Pick the days now: 'Tuesday, Thursday, Saturday.' Putting them on the calendar matters more than you'd think.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: you'll catch yourself wanting to walk a little longer. Week 4: fewer afternoon energy crashes. Month 2: stairs stop feeling like a workout.",
    avoid: "Buying new gear before you've earned the habit. Don't research the optimal route. Don't track your heart rate. Just walk.",
    red_flags: "Chest pain, severe shortness of breath, or dizziness during walks — stop and see a doctor before continuing.",
    tracking_metric: "Walks per week",
    review_days: 7,
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  WEIGHT & METABOLISM
  // ─────────────────────────────────────────────────────────────────────────

  BALANCED_MEALS: {
    category: 'weight_metabolism',
    difficulty: "easy",
    time_per_day: "0 min — composition change",
    coach_intro: "Most people overcomplicate eating. The truth is, almost every meal that works has the same three components: protein, fiber, and a little fat. That's the whole framework. Once you eat this way most of the time, blood sugar, cravings, and energy fix themselves.",
    this_week: "Build every meal around protein first. Then add fiber (vegetables or fruit) and a little fat. Don't measure, don't count — just check the plate before you eat.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Take a photo of every meal, snack, and drink today.",
      "Write down food before you eat it for one meal.",
      "Track drinks with calories for one day.",
      "Use a simple 1–10 hunger rating before dinner.",
      "Review photos at night and look for one easy swap.",
      "Remove one extra bite habit: finishing plates, grazing, or leftovers.",
      "Use a smaller plate for one meal.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. That's the foundation. Hold it. Once it's automatic, we add the next layer — meal timing.",
    if_did_partial: "Halfway. Which meal is hardest to balance? Usually it's breakfast or a snack — those are where the carb-only trap lives.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: steadier energy, fewer cravings. Week 4: less afternoon hunger spike. Month 2: weight starts moving in the right direction without effort.",
    avoid: "Counting macros to the gram. We're going for 'most meals look right,' not perfection.",
    red_flags: "Persistent unintended weight loss or gain despite balanced eating — get bloodwork done. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Meals that hit all three components",
    review_days: 14,
  },

  PROTEIN_INTAKE: {
    category: 'weight_metabolism',
    difficulty: "easy",
    time_per_day: "0 min — meal composition",
    coach_intro: "Protein is the most under-eaten and most consequential macronutrient for almost everyone over 30. It keeps you full, it preserves muscle, it stabilizes blood sugar. Most people get 40-60g a day; you probably need 100+. We're going to fix that gradually.",
    this_week: "25-30 grams of protein at breakfast. That's the assignment. The rest of the day stays however it usually is. We start with breakfast because it's the meal where most people fall short.",
    coach_story: "I co-ran a supplement store with a doctor for years, and one of the most consistent patterns we saw with patients was under-eating protein. They'd come in asking about a fancy supplement stack, and I'd ask, 'How much protein did you eat today?' Half the time the honest answer was 20-30 grams. The fix wasn't a supplement — it was eggs, yogurt, fish, chicken. Fancy comes after the basics work.",
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Stop eating at comfortably satisfied, not stuffed.",
      "Leave 3 bites behind once today if you are full.",
      "Identify the highest-calorie habit in your day without judging it.",
      "Add protein to breakfast or your first meal.",
      "Build one meal around eggs, Greek yogurt, fish, poultry, tofu, beans, or a shake.",
      "Put protein on the plate before starch.",
      "Use a protein snack instead of a sweet snack once.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold breakfast. This week aim for 25-30g at lunch and dinner too. Most people land at 90-120g daily that way.",
    if_did_partial: "Halfway. The trick is having a fast option pre-decided — Greek yogurt with nuts, three eggs, a shake. Decide once, eat repeatedly.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: less mid-morning hunger. Week 4: cravings drop noticeably. Month 2: easier maintenance of muscle as you age.",
    avoid: "Trying to hit a target weight in protein with chicken-and-rice meal-prep boredom. Variety keeps it sustainable.",
    red_flags: "Kidney disease — talk to your doctor before increasing protein significantly. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Grams of protein at breakfast",
    review_days: 7,
  },

  MEAL_TIMING: {
    category: 'weight_metabolism',
    difficulty: "easy",
    time_per_day: "0 min — schedule",
    coach_intro: "Your body runs on rhythms. Eating at unpredictable times is one of the easiest ways to throw blood sugar, hunger hormones, and sleep all out of sync. The fix isn't a strict schedule — it's a predictable one.",
    this_week: "Eat your three main meals within roughly the same 90-minute windows daily. So if breakfast is usually 7-8:30, hold that range. Lunch and dinner the same.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Track whether protein reduces cravings later.",
      "Add one fruit or vegetable to lunch.",
      "Add beans, lentils, oats, berries, or vegetables once today.",
      "Make half of one plate non-starchy vegetables.",
      "Use salad or vegetables before the main meal.",
      "Add fiber before removing food.",
      "Replace one sugary drink with water, unsweet tea, or zero-sugar option.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Predictability beats perfection here. Hold for two weeks before changing anything else.",
    if_did_partial: "Halfway. Which meal slips most? Usually it's lunch, eaten whenever work allows. That's the one to anchor first.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: hunger feels predictable instead of random. Week 4: less evening grazing.",
    avoid: "Long unintentional fasts followed by binges. Predictable beats heroic.",
    red_flags: "Severe hunger swings, dizziness when missing a meal — get checked for hypoglycemia or diabetes. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Days you hit all three meal windows",
    review_days: 14,
  },

  MEAL_SCHEDULE: {
    category: 'weight_metabolism',
    difficulty: "easy",
    time_per_day: "0 min — schedule",
    coach_intro: "Building a meal schedule is less about discipline and more about removing decisions. The more often you have to decide 'what should I eat?', the more often the answer is 'whatever's quickest.' That's how the wheels come off.",
    this_week: "Pick your standard breakfast — same thing most days. Pick 2-3 lunch options you rotate through. Make dinner the only meal that varies. That's the system.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Pick one dessert day instead of automatic dessert.",
      "Read one label for added sugar.",
      "Swap candy/cookies for fruit plus protein once.",
      "Move sweets out of arm’s reach, not out of your life forever.",
      "Choose one meal with protein + vegetables before refined carbs.",
      "Swap white bread/pasta/rice for whole-grain or smaller portion once.",
      "Keep carbs, but pair them with protein or fat.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Decision fatigue is a real thing — you've removed it from two meals daily. Hold the system, expand only if it stops feeling sustainable.",
    if_did_partial: "Halfway. Which meal broke down — probably lunch, because work pressure squeezes it. The fix: bring lunch most days. Boring works.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: less stress around food decisions. Week 4: better food choices on the days you didn't plan.",
    avoid: "Trying to do this with 5 lunch options. Two or three is the sweet spot.",
    red_flags: "If structure-around-food triggers anxiety or restriction patterns, talk to a professional. Eating disorders are real and serious. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Days the system held",
    review_days: 14,
  },

  EATING_WINDOW: {
    category: 'weight_metabolism',
    difficulty: "moderate",
    time_per_day: "0 min — when, not what",
    coach_intro: "An eating window is the simplest version of intermittent fasting — eat all your food in roughly the same 10-12 hour window each day, give your body 12-14 hours overnight to fully process. No fancy fasting. Just predictable.",
    this_week: "Pick a 12-hour eating window — say 8 AM to 8 PM. Eat all meals inside it. Outside the window: water, tea, or coffee only. That's the assignment.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Skip the carb-only snack today.",
      "Track energy after a lower-refined-carb lunch.",
      "Walk 5–10 minutes after one meal.",
      "Use the first 5 minutes after dinner as your walk cue.",
      "Walk indoors if weather is bad.",
      "Invite someone for the after-meal walk.",
      "Track digestion and energy after the walk.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold it for two weeks. If you want to tighten, you can move to a 10-hour window — but the 12 is doing most of the work for most people.",
    if_did_partial: "Halfway. Which end of the window slips — late dinner or early breakfast? Most often it's late snacking; that's the one to address first.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: better sleep (digestion isn't running at bedtime). Week 4: morning hunger feels normal instead of urgent.",
    avoid: "Tightening the window aggressively (8-hour, 6-hour) early on. The 12-hour does 80% of the benefit and 20% of the difficulty.",
    red_flags: "If you have diabetes, eating disorder history, are pregnant, or take medications that need food — talk to a doctor before changing meal timing.",
    tracking_metric: "Days you held the window",
    review_days: 14,
  },

  EATING_CUTOFF: {
    category: 'weight_metabolism',
    difficulty: "easy",
    time_per_day: "0 min — boundary",
    coach_intro: "Late-night eating is one of the most common saboteurs of sleep and weight. Your body wants to wind down, and your stomach is processing pizza. The fix isn't dramatic — it's a clean cutoff time most nights.",
    this_week: "No food after 8 PM (or 2-3 hours before your usual bedtime, whichever is earlier). Tea or water only. That's it.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Add 1,000 steps over your current baseline.",
      "Take one phone call walking.",
      "Park farther away once.",
      "Use stairs once if safe.",
      "Walk the long route to one routine destination.",
      "Do 2 sets of chair squats or sit-to-stands.",
      "Do wall push-ups for one minute.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Watch your sleep this week — most people see a noticeable difference. Hold it.",
    if_did_partial: "Halfway. The trap is the after-dinner snack 'just because.' Tea is the substitute that works.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: weird how often you reach for food at night out of habit. Week 2: sleep deeper. Week 4: morning hunger returns to normal.",
    avoid: "'Healthy' late-night snacks. Even a piece of fruit at 10 PM disrupts sleep more than no food.",
    red_flags: "If late-night eating is compulsive and feels out of control, it's worth talking to a professional. Night eating syndrome is real and treatable. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Nights you held the cutoff",
    review_days: 7,
  },

  EATING_STRUCTURE: {
    category: 'weight_metabolism',
    difficulty: "easy",
    time_per_day: "0 min — pattern",
    coach_intro: "If your eating is chaotic — meals at random times, grazing all day, sometimes huge dinners after long fasts — your body is constantly trying to figure out what's happening. We're going to give it a predictable pattern.",
    this_week: "Three meals. Two snacks if needed. Each one has protein. No grazing between. That's the structure.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Carry groceries or light weights with good form.",
      "Schedule one short strength session this week.",
      "Train muscles before chasing scale speed.",
      "Create a consistent first meal time for 3 days.",
      "Stop random grazing after dinner one night.",
      "Use a planned snack instead of open-ended snacking.",
      "Define your kitchen-closed time relative to bedtime.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Structure is doing the work. Hold it for two weeks before adding anything more sophisticated.",
    if_did_partial: "Halfway. Which meal got missed — usually breakfast. Even something small (a yogurt, a hard-boiled egg) counts.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: hunger feels predictable, not urgent. Week 4: less mindless snacking.",
    avoid: "Skipping breakfast and 'making up for it' with a huge lunch. Your body doesn't average out — it overcompensates.",
    red_flags: "If chaotic eating is part of a binge-restrict pattern, talk to a professional. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Days the structure held",
    review_days: 14,
  },

  CRAVINGS_PROTOCOL: {
    category: 'weight_metabolism',
    difficulty: "easy",
    time_per_day: "0 min — meal composition",
    coach_intro: "Cravings aren't a willpower problem — they're a signal. Almost always: you're under-eating protein, your blood sugar is swinging, or you're not sleeping enough. Address the upstream cause and the cravings shrink on their own.",
    this_week: "Three things: 25-30g protein at every meal, no skipped meals, and 7+ hours of sleep. We're not addressing the cravings directly — we're removing the conditions that create them.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Delay a craving for 10 minutes and reassess.",
      "Prep one protein option for tomorrow.",
      "Wash or cut fruit/vegetables so they are easy to grab.",
      "Plan one emergency meal that is better than takeout.",
      "Keep a protein snack at work or in the car.",
      "Make tomorrow’s lunch before tonight’s willpower disappears.",
      "Serve food from the kitchen, not family-style at the table.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Cravings should already be quieter. Hold it two more weeks — that's when most people notice cravings drop dramatically.",
    if_did_partial: "Halfway. Which leg is weakest — protein, meal-skipping, or sleep? Cravings track most closely with sleep, so that's where the next leverage usually is.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: cravings still happen but less intense. Week 4: notably quieter days.",
    avoid: "Trying to white-knuckle past cravings without fixing the upstream cause. That works for a week and then collapses.",
    red_flags: "Sudden severe cravings (especially for ice, dirt, non-foods) can signal iron deficiency. Persistent cravings + weight gain + fatigue → check thyroid. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Days all three held",
    review_days: 14,
  },

  DINNER_PROTOCOL: {
    category: 'weight_metabolism',
    difficulty: "easy",
    time_per_day: "5 min planning",
    coach_intro: "Dinner sets up the next day. A heavy late dinner means worse sleep, harder morning, and a crash at your afternoon dip the next afternoon. We're going to make dinner less of a daily wreck without making it joyless.",
    this_week: "Dinner finished by 8 PM. Protein-forward — meat, fish, beans, eggs — with vegetables. Carbs okay but secondary, not the main event.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Plate once, then pause before seconds.",
      "Use a palm-size protein and fist-size fiber target.",
      "Put snacks in a bowl instead of eating from the bag.",
      "Wait 10 minutes before deciding on more.",
      "Look at the menu before arriving.",
      "Choose protein first when ordering.",
      "Ask for sauce/dressing on the side once.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Watch your sleep this week — better dinner usually shows up there first. Add the post-dinner walk if you haven't yet.",
    if_did_partial: "Halfway. What's the dinner trap — late takeout, big carb-heavy meals, snacking after? Each has a different fix.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: deeper sleep, easier mornings. Week 4: clearer middle-of-the-day energy.",
    avoid: "Dessert that becomes the second meal. One bite is fine; a plate of it is the issue.",
    red_flags: "Heartburn or reflux with dinner most nights — see a doctor; chronic reflux is treatable and worth not living with. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Nights dinner met both criteria",
    review_days: 7,
  },

  NUTRITION_PROTOCOL: {
    category: 'weight_metabolism',
    difficulty: "easy",
    time_per_day: "0 min — composition",
    coach_intro: "Nutrition gets oversold and overcomplicated. The basics work for almost everyone: enough protein, plenty of vegetables, real food more than processed food, and hydration. Master those four and you've solved 80% of nutrition.",
    this_week: "Each day, hit four bases: protein at every meal, two servings of vegetables, mostly real food (not packaged), and 80 oz of water.",
    coach_story: "I ran a supplement store with a doctor for years, and we'd always tell patients: nail the basics first. The fancy stacks come later. We had so many people walk in asking for the latest peptide or nootropic when they hadn't eaten a vegetable in three days. Real food, enough protein, water — that does more than anything you'll find on a shelf.",
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Box half before you start if portions are huge.",
      "Enjoy the meal without turning it into a free-for-all.",
      "Ask: hungry, stressed, tired, or bored?",
      "Drink water and wait 10 minutes before a craving snack.",
      "Use a planned sweet instead of random grazing.",
      "Pair a craving food with protein, not shame.",
      "Notice the craving pattern, not your character.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The basics are doing their job. Hold it for two weeks before tinkering with anything else.",
    if_did_partial: "Halfway. Which base is hardest? Usually it's vegetables, because nobody craves broccoli. Lean on the easy ones — pre-cut bagged greens, frozen vegetables in the microwave.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: better digestion, steadier energy. Week 4: less afternoon hunger.",
    avoid: "Buying supplements before fixing the basics. Almost always a waste.",
    red_flags: "Sudden inability to eat or persistent digestive distress — see a doctor. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Days all four bases hit",
    review_days: 14,
  },

  ABDOMINAL_PROTOCOL: {
    category: 'weight_metabolism',
    difficulty: "moderate",
    time_per_day: "15-20 min",
    coach_intro: "Abdominal weight is the kind that's worth taking seriously — it sits next to organs and tracks with metabolic risk. The good news: it's also the kind that responds best to a few simple changes. Walk after meals, get protein right, sleep enough.",
    this_week: "Three things: 25-30g protein at breakfast, 10-minute walk after each meal, and 7+ hours of sleep. That's the trio specifically for abdominal weight.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Pick one weekend meal to track honestly.",
      "Keep breakfast structured on Saturday or Sunday.",
      "Walk after the largest weekend meal.",
      "Do not let one indulgence become a weekend spiral.",
      "Plan Monday’s breakfast before Sunday night.",
      "Protect 7+ hours of sleep opportunity once this week.",
      "Notice whether poor sleep drives hunger.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold these for at least 8 weeks — abdominal changes are slow and worth the patience.",
    if_did_partial: "Halfway. Which leg is hardest? Usually it's the post-meal walk after dinner. That's the most consequential one.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: clothes fit a little differently. Month 2-3: visible changes. Month 6: meaningful shift in metabolic markers.",
    avoid: "Targeted ab exercises and crunches. They build muscle, not lose fat. Whole-body changes are what reduce abdominal fat.",
    red_flags: "Sudden abdominal weight gain unrelated to lifestyle — get checked for hormonal or thyroid issues. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Days all three habits held",
    review_days: 14,
  },

  BODY_AWARENESS: {
    category: 'weight_metabolism',
    difficulty: "easy",
    time_per_day: "5 min",
    coach_intro: "Most people are disconnected from their own body — they don't know how clothes fit week to week, when energy dips, or what foods make them feel different. We're going to start paying attention. Nothing fancy. Just noticing.",
    this_week: "Once a week, in the same outfit, same time of day: stand in front of a mirror for 30 seconds. Notice. Don't judge. That's the assignment.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Use a wind-down instead of late snacking once.",
      "Move caffeine earlier to protect sleep.",
      "Do not cut calories harder after bad sleep; get steady.",
      "Use 2 minutes of breathing before stress snacking.",
      "Write the stressor before deciding to eat.",
      "Take a 5-minute walk before the snack.",
      "Keep a satisfying planned snack available.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Awareness drives change more than tracking does. Hold this — it takes 4-6 weeks to start really noticing patterns.",
    if_did_partial: "The miss is usually the mirror part — feels weird, feels uncomfortable. Just 30 seconds. Even if you don't 'do' anything with it.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: you'll notice patterns you'd been ignoring. Week 4: small changes feel intuitive instead of forced.",
    avoid: "Daily weigh-ins. They're noise more than signal. Weekly is enough.",
    red_flags: "If body awareness triggers obsessive checking or distress, scale back. There's a line between awareness and disorder; respect it. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Weekly mirror check completed",
    review_days: 14,
  },

  WEIGHT_LOSS_GOAL: {
    category: 'weight_metabolism',
    difficulty: "moderate",
    time_per_day: "Built into meals + 30 min movement",
    coach_intro: "If weight loss is your goal, the fastest, most sustainable path is the most boring one: enough protein, lots of vegetables, walking, and sleep. Anything more aggressive than that — heavy restriction, intense exercise — usually gets results that don't stick. We're playing a long game here.",
    this_week: "Four levers: 25-30g protein at every meal, no calorie liquid except water/coffee/tea, 7,000+ steps daily, 7+ hours of sleep. That's the whole plan.",
    coach_story: null,
    daily_micro_wins: [
      "Take a photo or short note of every meal, snack, and drink for 7 days.",
      "Replace one sugary drink, dessert, or refined-carb snack with a protein + fiber option.",
      "Walk 10–20 minutes after one meal, or add 2,000 steps if walking is already easy.",
    ],
    weekly_additions: [
      null,
      "Ask for support instead of using food as the only tool.",
      "Weigh at the same time once or twice this week.",
      "Track waist measurement once per month, not daily.",
      "Look at trend, not one weigh-in.",
      "Use adherence metrics alongside body weight.",
      "Celebrate consistency before scale change.",
      "Reintroduce one carb intentionally, not randomly.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. This works — slowly, sustainably. Hold for 8 weeks before changing anything. The body needs that long to recalibrate.",
    if_did_partial: "Halfway. Which lever broke first? Usually it's the calorie liquid — soda or alcohol. That alone often stalls progress.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2-3: clothes fit a little better. Month 2-3: visible loss, 1-2 lbs/week steady. Month 6: 25-50 lb territory if you stuck with it.",
    avoid: "Quick fixes — keto, OMAD, juice fasts, etc. The data is clear: slow, balanced, sustainable wins long-term.",
    red_flags: "Rapid unintentional weight loss, weight loss + fatigue + thirst, or losing weight despite no behavior change — see a doctor immediately. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Days all four levers hit + weekly weight",
    review_days: 7,
  },

  MUSCLE_GAIN_GOAL: {
    category: 'weight_metabolism',
    difficulty: "moderate",
    time_per_day: "45 min training, 3-4× week",
    coach_intro: "Building muscle as an adult — especially over 40 — is one of the highest-return investments you can make. Strong, muscled people stay independent longer, recover faster from illness, and feel better overall. Building it requires three things: enough protein, progressive resistance training, and sleep.",
    this_week: "Three resistance training sessions this week (gym, bodyweight, bands — your choice). Protein intake bumps to 0.7-1g per pound of bodyweight. 7+ hours of sleep.",
    coach_story: null,
    daily_micro_wins: [
      "Eat a protein-forward meal after training or at your next meal.",
      "Complete one short strength session or 2 sets of a safe body-weight movement.",
      "Track body weight or waist once this week, not every hour.",
    ],
    weekly_additions: [
      null,
      "Add back fruit, beans, oats, or potatoes before sweets.",
      "Notice energy, cravings, and digestion after reintroduction.",
      "Keep portions reasonable while testing.",
      "Keep the food log during reintroduction.",
      "Keep one meal structure that worked.",
      "Keep one walk anchor that felt easiest.",
      "Keep strength twice weekly as weight comes down.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Growth happens between sessions — the rest matters. Hold this for 8 weeks before changing the routine.",
    if_did_partial: "Halfway. Which got skipped — sessions or protein? Sessions skipped: schedule them like meetings. Protein skipped: pre-decide your sources.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: lifts feel easier; recovery is faster. Month 2: visible changes. Month 6-12: meaningful muscle gain, especially if you're new to training.",
    avoid: "Going hard 5 days/week with no recovery. Your muscles grow on rest days, not training days.",
    red_flags: "New joint pain that lasts more than a week, or pain that worsens during a movement → stop and get evaluated. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Sessions per week + protein hit",
    review_days: 7,
  },

  COMPOSITION_GOAL: {
    category: 'weight_metabolism',
    difficulty: "moderate",
    time_per_day: "30-45 min",
    coach_intro: "Body composition — the ratio of muscle to fat — matters more than the number on the scale. Two people at the same weight can be built completely differently. Improving composition means building muscle and reducing fat at the same time, which is doable with the right inputs.",
    this_week: "Three things: resistance training 3 days, protein 1g/lb of target weight, and a 10-minute walk after meals. We're building muscle and losing fat at the same time — that takes both halves.",
    coach_story: null,
    daily_micro_wins: [
      "Log meals with a photo or simple food diary — no judging, just data.",
      "Build one meal around protein + fiber before adding extras.",
      "Walk 10 minutes after one meal or add one movement break to lower glucose exposure.",
    ],
    weekly_additions: [
      null,
      "Plan a high-risk situation before it happens.",
      "Use the 80% routine — not perfect, just repeatable.",
      "Take a photo of every meal, snack, and drink today.",
      "Write down food before you eat it for one meal.",
      "Track drinks with calories for one day.",
      "Use a simple 1–10 hunger rating before dinner.",
      "Review photos at night and look for one easy swap.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Composition changes are slow but they happen. Hold for 12 weeks before judging.",
    if_did_partial: "Halfway. Composition is the hardest goal because it requires both halves. Pick one to lock in first — usually training, then add protein.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Month 2: clothes fit differently even if scale moves slowly. Month 4-6: real visual change. Month 12: a different body.",
    avoid: "Crash dieting. It tears down muscle along with fat — you end up smaller but weaker.",
    red_flags: "Significant strength loss over weeks despite consistent training → could indicate over-training, under-eating, or medical issue. Talk with your clinician first if pregnant, on diabetes medication, have an eating-disorder history, or have rapid/unexplained weight change.",
    tracking_metric: "Sessions + protein + post-meal walks",
    review_days: 14,
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  STRESS & MENTAL HEALTH
  // ─────────────────────────────────────────────────────────────────────────

  STRESS_PROTOCOL: {
    category: 'stress_mental',
    difficulty: "easy",
    time_per_day: "5 min",
    coach_intro: "Stress isn't going away. The goal isn't to eliminate it — it's to give your nervous system real recovery moments throughout the day so it doesn't compound. Five minutes of deliberate down-shift, two or three times a day, does more than an hour of meditation once a week.",
    this_week: "Two deliberate downshifts per day. Each one: five minutes of slow breathing or a quiet walk. Pick the times — usually one mid-morning and one mid-afternoon work best.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Take 5 slow breaths before responding to stress.",
      "Do two physiological sighs.",
      "Relax jaw and shoulders three times today.",
      "Put both feet on the floor and name 5 things you see.",
      "Use a 2-minute breathing timer before bed.",
      "Protect one 10-minute phone-free break.",
      "Schedule one 30-minute recovery block this week.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. That's the rhythm. Hold it. This week, add a third — right when you get home from work. The transition zones are where most stress compounds.",
    if_did_partial: "Halfway. The trap is treating downshifts as optional. Schedule them — phone alarms work best.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: less reactive to small irritations. Week 4: small things that used to ruin a day don't.",
    avoid: "Trying to add a 30-minute meditation practice. It fails by Thursday. Five minutes, repeated, wins.",
    red_flags: "If you're having panic attacks, persistent dread, or feel out of control — talk to a professional. Stress management is for stress; anxiety disorders need real treatment, and they have it.",
    tracking_metric: "Days you completed both downshifts",
    review_days: 7,
  },

  STRESS_MANAGEMENT: {
    category: 'stress_mental',
    difficulty: "easy",
    time_per_day: "5 min",
    coach_intro: "Stress isn't going away. Anyone who tells you otherwise is selling something. The goal isn't to eliminate it — it's to give yourself recovery moments so it doesn't compound. Five minutes a day is enough to start.",
    this_week: "Five minutes a day where you're not on a screen, not doing a task, not thinking about the next thing. That's it. Sitting outside, deep breathing, a quiet walk — you pick. Just five minutes.",
    coach_story: "WOW. Have I been super stressed in my life. Work, family, divorce — sometimes you don't feel like you get a break. I've owned several businesses where I was responsible for not just myself, but a lot of employees and their families. That kind of pressure is hard to describe. The aha moment for me came when I was selling and training doctors on Autonomic Nervous System testing — equipment that measured Heart Rate Variability. One of the diagnostic results showed how much chronic stress someone was carrying. People were amazed when I could spot it just from looking at how their heart was behaving. Stress can be very destructive — but here's what I learned that changed everything. You can activate your parasympathetic nervous system on demand to dial the stress response back. The simplest way: a 5-second inhale, then a 5-second exhale. Do that for a couple of minutes and your heart rate drops, and you calm down with it. I started combining this with 5-10 minutes of meditation whenever I felt overwhelmed. That feeling shrinks. You can function again. It's a tool I wish more people knew about.",
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Take a screen-free meal.",
      "Sit outside for 5 minutes with no task.",
      "Do not turn recovery into another project.",
      "Say no to one optional request.",
      "Delay an answer: 'Let me check and get back to you.'",
      "Turn off one notification.",
      "End work at a planned stop point once.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. That's the foundation. This week, see if you can stretch to 10 minutes one of those days. Not all of them — just one. We're growing this slowly on purpose.",
    if_did_partial: "Halfway. What's eating those minutes? Usually it's not time, it's permission — most people feel selfish taking 5 minutes for themselves. That's exactly why you need to.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: slightly less reactive when small things go wrong. Week 4: small things stop ruining your day. Month 2: you'll notice when you skip it, because you'll feel it.",
    avoid: "Trying to add a 30-minute meditation practice on top of an already-packed day. That fails by Thursday. Smaller is sticky.",
    red_flags: "If you're having panic attacks, persistent low mood, or feeling unsafe — please talk to a professional. Stress management is for stress. Anxiety and depression are different problems and they have real treatments.",
    tracking_metric: "Days you took 5 minutes of quiet",
    review_days: 7,
  },

  BURNOUT_PREVENTION: {
    category: 'stress_mental',
    difficulty: "moderate",
    time_per_day: "Schedule-level changes",
    coach_intro: "Burnout doesn't usually arrive all at once. It accumulates — late nights, weekends spent catching up, never quite recovering. By the time you feel it, you're already deep in. This protocol is for people who can feel it coming and want to head it off.",
    this_week: "Three boundaries this week. One: a hard stop time on weekday work. Two: one fully unscheduled evening. Three: a weekend morning that isn't 'productive.' All three.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Do not explain a boundary more than needed.",
      "Write your top 3 tasks only.",
      "Move one non-urgent task to another day.",
      "Ask what actually has to be done today.",
      "Choose a 'good enough' finish for one item.",
      "Stop working when the planned block ends.",
      "Text one person you trust.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The boundaries are doing the work. Hold them. They're not a one-week reset — they're the new floor.",
    if_did_partial: "Halfway. Which boundary broke? Usually it's the weekday cutoff — work bleeds. Set a phone alarm for the cutoff this week.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: the guilt around resting starts to ease. Week 4: more energy across the week, not just at the start.",
    avoid: "Saving recovery for vacation. Vacations don't fix burnout — daily and weekly boundaries do.",
    red_flags: "Persistent exhaustion that doesn't improve with rest, loss of joy in things you used to enjoy, irritability, hopelessness — see a doctor or therapist. Burnout and depression can look similar.",
    tracking_metric: "Boundaries held per week",
    review_days: 7,
  },

  BURNOUT_RECOVERY: {
    category: 'stress_mental',
    difficulty: "moderate",
    time_per_day: "Schedule-level",
    coach_intro: "If you're already in burnout, the answer isn't a productivity hack — it's permission to slow down. Recovering takes time and it requires deliberate non-doing. The work is real but it looks like the opposite of work.",
    this_week: "Two non-negotiables: 7+ hours of sleep every night, and one full day off (or at least an evening + a morning) where nothing is scheduled. Phone off if possible.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Ask for help with one specific thing.",
      "Take a walk with someone.",
      "Tell someone the honest version, not the polished version.",
      "Schedule one connection before you feel isolated.",
      "Start a 10-minute wind-down tonight.",
      "Write down worries before bed.",
      "Keep phone out of bed once.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Burnout recovery takes 4-8 weeks of consistent rest, not one good week. Hold the floor.",
    if_did_partial: "Halfway. Sleep usually goes first under burnout. The fix isn't more discipline — it's a hard cutoff time on the day before.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: you'll feel guilty about resting. That's normal. Week 4: guilt softens. Week 8: real recovery starts to take.",
    avoid: "'Productive recovery.' Recovery isn't a project. Don't optimize it.",
    red_flags: "Persistent exhaustion + low mood + hopelessness for more than a few weeks — see a doctor or therapist. This is treatable.",
    tracking_metric: "Sleep hours + days off protected",
    review_days: 7,
    escalation: "If burnout includes hopelessness, lack of joy, or thoughts of self-harm, please reach out to a mental health professional. This is not weakness — it's a medical condition with real treatments.",
  },

  MOOD_SUPPORT: {
    category: 'stress_mental',
    difficulty: "easy",
    time_per_day: "15-30 min",
    coach_intro: "When mood is low, the things that help are usually the things you don't feel like doing — moving, getting outside, connecting with someone. We're going to start so small that the activation energy is almost zero.",
    this_week: "Three small things daily: 10 minutes outside, one text or call to a person you care about, and one act of completed-something (a shower, a meal cooked, a small task done). That's it.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Move caffeine earlier to reduce nighttime anxiety.",
      "Treat sleep as recovery, not a reward.",
      "Walk 5–10 minutes when stress spikes.",
      "Use stairs or a brisk lap to discharge adrenaline.",
      "Stretch neck and hips for 2 minutes.",
      "Do gentle movement on high-stress days instead of skipping all movement.",
      "Notice mood before and after the walk.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. These are the levers. Mood improvement is rarely a single thing — it's the small consistent inputs.",
    if_did_partial: "Halfway. Which one is hardest? Usually the social one. Even a one-line text counts.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: small lifts — moments of feeling normal. Week 4: more good days than bad. Month 2: a different baseline.",
    avoid: "Waiting until you 'feel like it.' Mood follows action, not the other way around.",
    red_flags: "Persistent low mood for 2+ weeks, hopelessness, thoughts of self-harm — please talk to a doctor or therapist this week. Depression is treatable, often dramatically.",
    tracking_metric: "Days you hit all three",
    review_days: 7,
    escalation: "Persistent depression deserves real treatment — therapy, medication, or both. There is no virtue in suffering through it without help.",
  },

  BOUNDARY_SETTING: {
    category: 'stress_mental',
    difficulty: "moderate",
    time_per_day: "In-the-moment",
    coach_intro: "Boundaries aren't selfish — they're how you stay capable of helping anyone, including yourself. People-pleasers carry the weight of every 'yes' they should have said no to. We're going to practice one boundary this week.",
    this_week: "Pick one: a work boundary, a family boundary, or a friend boundary. Hold it for the whole week. Just one. The practice matters more than the size.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Stand outside for 5 minutes.",
      "Look at trees, sky, or water without a phone.",
      "Take one meeting or call outdoors if possible.",
      "Eat one snack outside.",
      "Use daylight as a stress reset.",
      "Check news once instead of repeatedly.",
      "Move social apps off the home screen.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. That's the muscle. Same boundary next week, or pick another. Boundaries get easier the more you practice.",
    if_did_partial: "Halfway. Where did it break — guilt, pressure, or just forgetting? Each has a different fix.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: less resentment toward people who used to drain you. Week 4: more capacity for the things that matter.",
    avoid: "Over-explaining. 'No' is a complete sentence. So is 'I can't this week.'",
    red_flags: "If setting boundaries triggers severe anxiety or guilt, talking to a therapist about people-pleasing patterns is worth it.",
    tracking_metric: "Days the boundary held",
    review_days: 7,
  },

  PROFESSIONAL_SUPPORT: {
    category: 'stress_mental',
    difficulty: "moderate",
    time_per_day: "One appointment",
    coach_intro: "Some things shouldn't be a self-improvement project. Anxiety that interferes with your life, depression that won't lift, trauma that keeps surfacing — those need a professional, not a podcast or an app. The hardest part is the first call. Once you make it, the rest gets easier.",
    this_week: "Make one call or send one portal message — to a therapist, your primary care doctor, or a mental health line. That's the whole assignment for this week.",
    coach_story: null,
    daily_micro_wins: [
      "Contact a therapist, doctor, crisis line, or trusted support person today.",
      "Remove one optional demand from today — no explanations required.",
      "Do one grounding action: breathe, walk, eat, or sit outside for 5 minutes.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The call is the win — the rest follows. Show up to the appointment. Most people feel better just from the act of asking for help.",
    if_did_partial: "What's the block — cost, time, fear of being seen? Each has a workaround. Tell me which.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Most people feel a little lighter just from making the appointment.",
    avoid: "Telling yourself you'll call 'when it gets bad enough.' If you're considering it, it's already bad enough.",
    red_flags: "If you're having thoughts of harming yourself, call or text 988 (Suicide & Crisis Lifeline) right now. Don't wait. Don't try to handle it alone.",
    tracking_metric: "Outreach completed (yes/no)",
    review_days: 7,
    escalation: "Mental health is health. Asking for help is not a failure — it's the most efficient action you can take. Your future self will thank you.",
  },

  LIFE_RESTRUCTURING: {
    category: 'stress_mental',
    difficulty: "intense",
    time_per_day: "Major decision time",
    coach_intro: "Sometimes the issue isn't the way you're handling your life — it's the life. A job that's eating you, a relationship that's draining, a city that doesn't fit. Restructuring is hard work but sometimes it's the only honest answer. This protocol is about getting clarity, not making the decision overnight.",
    this_week: "Spend 30 minutes — one sitting, alone — answering: What is one thing in my life I would change if I could? What's stopping me? Write the answers down. That's it.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Notice the all-or-nothing thought and choose one small action.",
      "Rate exhaustion 1–10 daily.",
      "Track loss of joy or emotional numbness.",
      "Name one thing you are over-committed to.",
      "Schedule one recovery block before burnout escalates.",
      "Ask for professional support if recovery is not working.",
      "Save your doctor/therapist/crisis contact in your phone.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. You have clarity. This week, share it with one trusted person — partner, friend, therapist. Not to make decisions, just to think out loud.",
    if_did_partial: "What stopped the writing — fear of what would surface, or just busyness? Both are signals worth noticing.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Clarity often arrives the moment you write the question down — even before you have an answer.",
    avoid: "Making big decisions in a single moment of clarity. Sit with it for at least a month before acting.",
    red_flags: "If big-picture restructuring is driven by depression or crisis, address those first with a professional. Don't quit your job during a depressive episode.",
    tracking_metric: "30-minute reflection completed",
    review_days: 14,
  },

  PRIORITIZATION: {
    category: 'stress_mental',
    difficulty: "easy",
    time_per_day: "10 min",
    coach_intro: "Most overwhelm isn't from too many things to do — it's from no priority order. When everything is urgent, nothing is. We're going to fix that with a simple weekly habit: pick three.",
    this_week: "Each Monday morning, pick three priorities for the week. Not ten. Three. Write them down where you'll see them all week.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Send one portal message if symptoms are persistent.",
      "Use 988 if self-harm thoughts appear.",
      "Tell a trusted person if you cannot function.",
      "Do not treat crisis as a coaching issue.",
      "Add 10 minutes between two commitments.",
      "Show up 5 minutes early once to reduce rushing.",
      "Leave one blank space on the calendar.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Three is the magic number — enough to be meaningful, few enough to actually finish. Hold the habit.",
    if_did_partial: "Half the value. Even just picking the three on Monday is most of the benefit, even if you didn't reference them.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: end-of-week feels less chaotic. Week 4: things that mattered actually got done.",
    avoid: "A list of 12. Three. The discipline of cutting is the value.",
    red_flags: "If you can't identify three priorities because everything truly is on fire — that's an early burnout signal. See BURNOUT_PREVENTION.",
    tracking_metric: "Mondays you set the three",
    review_days: 7,
  },

  TIME_MANAGEMENT: {
    category: 'stress_mental',
    difficulty: "easy",
    time_per_day: "5-10 min planning",
    coach_intro: "Time management isn't about tools — it's about deciding what gets your attention. Most people fill their calendar with what other people want and wonder why nothing important gets done. We're going to fix that with one habit.",
    this_week: "Each morning, before email or anything else: 5 minutes deciding what the most important thing of the day is. Then do that first. Email and meetings come after.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Do one transition breath between work and home.",
      "Stop stacking obligations without recovery.",
      "Write one thing that did not go wrong today.",
      "Thank one person specifically.",
      "Notice one small win and let it count.",
      "Connect one hard task to why it matters.",
      "Do not use gratitude to deny real problems.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. That's the lever. Hold it. The discipline of doing the right thing first beats any productivity system.",
    if_did_partial: "Halfway. The trap is opening email first 'just to check.' That hijacks your day before it starts.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: more sense of accomplishment at end of day. Week 4: less reactive, more directed.",
    avoid: "Productivity systems that take more time to maintain than they save.",
    red_flags: "If your time problems persist despite real effort, look upstream — too many commitments, no boundaries, working on the wrong things entirely.",
    tracking_metric: "Days you started with planning",
    review_days: 7,
  },

  RECOVERY_PROTOCOL: {
    category: 'stress_mental',
    difficulty: "easy",
    time_per_day: "10-15 min",
    coach_intro: "When you're running on fumes, the answer isn't a productivity hack. It's permission to slow down. I've been there myself, and the work of recovering is real work — just different from what you're used to.",
    this_week: "Practice three small recovery techniques this week. They take minutes, not hours, and they work whether you sleep at 9 PM or 1 AM. The goal is to give your nervous system real downshift moments throughout the day.",
    coach_story: "I've always been a bit of a gym rat, and my work ethic was off the charts. It wasn't unusual for me to burn the candle at both ends until I'd crash and have to take an entire day off just to recover. As an avid podcast listener, I caught Andy Galpin (PhD in Human Performance) talking with Dr. Huberman about overtraining and how important recovery is to every part of your life. Your body can't get stronger if you're constantly taxing it. Your mind is the same. The good news: you don't have to overhaul your schedule. You just have to insert real recovery moments into the schedule you already have.",
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Try 4-second inhale, 6-second exhale for 2 minutes.",
      "Use box breathing for one stressful moment.",
      "Exhale longer than inhale before a difficult conversation.",
      "Pair breathing with walking if sitting still is hard.",
      "Stop if breathing practice makes panic worse and use grounding instead.",
      "Ask for one household task to be shared.",
      "Create a 10-minute decompression window after work.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "That's the foundation. Your nervous system gets stronger with these reps, just like a muscle. Keep going — small consistent doses beat heroic occasional efforts every time.",
    if_did_partial: "Halfway is real progress. Notice which technique was easiest — that's your anchor. Build the others around it.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: small noticing-it-helping moments. Week 2: less reactive when stressors hit. Week 4: genuinely calmer baseline. Month 2: you'll feel it when you skip a day.",
    avoid: "'Productive recovery.' Recovery isn't a project. Don't optimize it, don't track every minute, don't make it another stressful goal. Just practice.",
    red_flags: "Persistent exhaustion lasting more than 4 weeks, loss of joy in things you used to enjoy, hopelessness, can't get out of bed — that's depression-adjacent territory. Please talk to a doctor or therapist. This is the kind of thing that gets dramatically better with help.",
    tracking_metric: "Days you practiced the techniques",
    review_days: 7,
  },

  RECOVERY_TIME: {
    category: 'stress_mental',
    difficulty: "easy",
    time_per_day: "30-60 min",
    coach_intro: "Recovery isn't a luxury — it's when adaptation happens. Whether the load is physical (training), mental (work), or emotional (life stress), your body and brain need recovery time to come back stronger. Otherwise you accumulate damage.",
    this_week: "Build in 30-60 minutes daily of true downtime. Not scrolling, not catching up on work, not multi-tasking. Reading, walking, sitting, talking — your choice. Single-tasking only.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Do not walk into the house on your phone once.",
      "Use a simple phrase: 'I need 10 minutes, then I’m available.'",
      "Plan one easy meal on a hard day.",
      "Set a 15-minute admin block.",
      "Open the bill or form, even if you do not finish it.",
      "Write the next action on paper.",
      "Ask for help with one confusing task.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold it. This is the difference between people who thrive long-term and people who burn out.",
    if_did_partial: "Halfway. Which days got skipped — usually the busy ones, but those are the ones that need it most.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: less mental fatigue at end of day. Week 4: better sleep, easier mornings.",
    avoid: "Treating recovery as 'wasted time.' It's the most productive thing you can do for tomorrow.",
    red_flags: "If you can't recover even with deliberate downtime — exhaustion persists — get a medical checkup.",
    tracking_metric: "Days you protected recovery time",
    review_days: 7,
  },

  PRIORITY_RECOVERY: {
    category: 'stress_mental',
    difficulty: "moderate",
    time_per_day: "Schedule-level",
    coach_intro: "Sometimes recovery has to be the priority — not just one thing on the list, but the thing. If you're approaching burnout, dealing with a major life event, or coming off illness, this is the protocol.",
    this_week: "Three priorities, all about recovery: 8+ hours of sleep, no overtime/extra commitments, one full day with nothing scheduled. Everything else gets deprioritized this week.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Stop after the timer and schedule the next step.",
      "Name the emotion without fixing it.",
      "Journal for 5 minutes longhand.",
      "Take a slow walk before making a major decision.",
      "Share the real issue with one trusted person.",
      "Seek professional support for grief, trauma, or persistent depression.",
      "Choose the smallest action for bad days: breathe, walk, eat, sleep.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold this for 2-3 weeks at minimum. Recovery from severe load isn't a one-week thing.",
    if_did_partial: "Halfway. Which leg broke? Usually it's the 'no to optional' part — guilt drives the yes. The fix is recognizing the guilt and saying no anyway.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: starting to feel human again. Week 4: real recovery taking hold. Month 2: a different baseline.",
    avoid: "Compressing recovery into 'one weekend.' That's not recovery, that's a pause.",
    red_flags: "Persistent exhaustion + low mood + hopelessness despite priority recovery — talk to a professional.",
    tracking_metric: "Days all three held",
    review_days: 7,
    escalation: "Burnout that doesn't improve with priority recovery is medical. Please see a doctor.",
  },

  URGENT_RECOVERY: {
    category: 'stress_mental',
    difficulty: "intense",
    time_per_day: "Schedule clear",
    coach_intro: "If you're reading this protocol, you're probably already past warning signs and into the 'I can't keep going' zone. The work now is to stop. Not slow down — stop. Real recovery starts with permission to do nothing for long enough.",
    this_week: "Two days off this week, minimum. Real days off. No work, no errands, no productive 'rest projects.' Sleep, eat, walk, sit. That's the plan. Tell anyone who needs to know that you'll be unavailable.",
    coach_story: null,
    daily_micro_wins: [
      "Contact a therapist, doctor, crisis line, or trusted support person today.",
      "Remove one optional demand from today — no explanations required.",
      "Do one grounding action: breathe, walk, eat, or sit outside for 5 minutes.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. You're not done — recovery from this state takes weeks, not days. Schedule next week's days off now.",
    if_did_partial: "Even one day counts. What stopped the second one — work pressure, family, your own guilt? Each is a fix-it.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Day 1: weird, antsy. Day 2: starting to feel real rest. Week 2: clearer thinking returns. Month 1+: recovery starts to take.",
    avoid: "Returning to full speed when you start feeling better. The 'feeling better' is fragile until it's been there 4-6 weeks.",
    red_flags: "Hopelessness, thoughts of self-harm, inability to function — see a doctor or call 988 (Suicide & Crisis Lifeline) immediately.",
    tracking_metric: "Days off taken + sleep hours",
    review_days: 3,
    escalation: "Severe burnout is a medical condition with real treatments. Please see a doctor or therapist this week.",
  },

  SCHEDULE_RECOVERY: {
    category: 'stress_mental',
    difficulty: "moderate",
    time_per_day: "Calendar-level",
    coach_intro: "If recovery never makes it onto your schedule, it doesn't happen. Period. We're going to put recovery in your calendar with the same weight as a meeting.",
    this_week: "Block four recovery times in your calendar — non-negotiable. Could be: morning quiet time, lunch walk, after-work transition, weekend morning. You pick. Block them.",
    coach_story: null,
    daily_micro_wins: [
      "Practice 2 minutes of slow breathing or physiological sighs.",
      "Protect one 10–15 minute phone-free recovery block.",
      "Make one tiny boundary or connection move: say no, ask for help, or text one person.",
    ],
    weekly_additions: [
      null,
      "Schedule connection before isolation builds.",
      "Get support early, not only when things break.",
      "Take 5 slow breaths before responding to stress.",
      "Do two physiological sighs.",
      "Relax jaw and shoulders three times today.",
      "Put both feet on the floor and name 5 things you see.",
      "Use a 2-minute breathing timer before bed.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The calendar is doing the work. Hold the system. After 4 weeks, this becomes automatic.",
    if_did_partial: "Halfway. Which block keeps getting moved? Whatever it is, that's the one to make 'unmovable' — meetings can move; that one can't.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: less reactive days. Week 4: more energy across the week.",
    avoid: "Treating calendar blocks as 'aspirational.' If you're going to skip them, take them off the calendar — at least be honest.",
    red_flags: "If recovery on the schedule still doesn't happen, it's worth examining whether something deeper is driving the over-commitment.",
    tracking_metric: "Recovery blocks honored per week",
    review_days: 14,
  },

  URGENT_PROFESSIONAL_SUPPORT: {
    category: 'stress_mental',
    difficulty: "easy",
    time_per_day: "Today",
    coach_intro: "Mental health emergencies don't wait. If you're in crisis — thoughts of self-harm, severe depression, a panic spiral that won't ease — please reach out today.",
    this_week: "Today: call or text 988 (the Suicide & Crisis Lifeline). Or call your doctor's emergency line. Or go to the ER. Help exists; the call is the hardest step and the most important.",
    coach_story: null,
    daily_micro_wins: [
      "Contact a therapist, doctor, crisis line, or trusted support person today.",
      "Remove one optional demand from today — no explanations required.",
      "Do one grounding action: breathe, walk, eat, or sit outside for 5 minutes.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Stay connected. Mental health crises pass with the right support.",
    if_did_partial: "Even a text counts. The next step is talking to someone trained.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "The first call is the hardest. Everything after gets easier.",
    avoid: "Trying to handle a mental health crisis alone. You don't have to.",
    red_flags: "Thoughts of self-harm or suicide — call or text 988 right now. If you have a plan or means, call 911 or go to the ER.",
    tracking_metric: "Reached out (yes/no)",
    review_days: 1,
    escalation: "If you're in immediate crisis: call 988 or 911. Help is available 24/7. Mental health emergencies are real medical emergencies and they have real treatments.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  BRAIN PERFORMANCE
  // ─────────────────────────────────────────────────────────────────────────

  FOCUS_PROTOCOL: {
    category: 'brain_performance',
    difficulty: "easy",
    time_per_day: "90 min focus block",
    coach_intro: "Focus is a trainable skill. Most people have lost it not because something is wrong with their brain, but because they've trained themselves into constant distraction. We're going to rebuild it the way you'd rebuild any muscle — small, consistent reps.",
    this_week: "One 90-minute focus block per day. Phone in another room, notifications off, single task. The first few days are hard. Your brain will scream for stimulation. That's the muscle being worked.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 20–25 minute single-focus block with notifications off.",
      "Take a 10-minute walk or light break before your hardest thinking work.",
      "Practice active recall: summarize one thing you learned without looking.",
    ],
    weekly_additions: [
      null,
      "Do one 20-minute focus block with notifications off.",
      "Put your phone in another room during focus.",
      "Define the task before the timer starts.",
      "Use one browser tab if possible.",
      "End with a one-sentence summary of what got done.",
      "Write down the top 3 distractions today.",
      "Remove one app from your home screen.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold it. By week 4, the focus muscle starts to feel automatic. Then you can add a second daily block.",
    if_did_partial: "Halfway. Which days got missed — usually the busy ones, but those are when the focus block is most valuable, not least.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: it feels brutal. Week 2: starts feeling natural. Week 4: clearer thinking, better work output, more sense of accomplishment.",
    avoid: "Trying to multitask during the block. Multitasking is the enemy you're training against.",
    red_flags: "Persistent inability to focus despite removing distractions, especially with other cognitive symptoms — get evaluated. Could be ADHD, sleep, or a medical issue.",
    tracking_metric: "Focus blocks completed per week",
    review_days: 7,
  },

  BRAIN_FOG_PROTOCOL: {
    category: 'brain_performance',
    difficulty: "easy",
    time_per_day: "15-30 min",
    coach_intro: "Brain fog has signatures. Most of the time it's sleep, blood sugar, or stress, in that order. Sometimes it's hydration. Rarely is it a 'cognitive' problem in the way people fear. We're going to fix the upstream causes one at a time and see what's left.",
    this_week: "Three things: 7+ hours of sleep, protein at every meal, 16 oz of water on waking and another by noon. That's the trio that addresses 80% of brain fog.",
    coach_story: "I sold a cognitive testing system used on thousands of patients across many practices. The pattern I saw over and over: people who came in worried about cognitive decline almost always had sleep, blood sugar, or stress at the root. Real dementia exists, but most fog isn't that — it's a body running on insufficient inputs.",
    daily_micro_wins: [
      "Do one 20–25 minute single-focus block with notifications off.",
      "Take a 10-minute walk or light break before your hardest thinking work.",
      "Practice active recall: summarize one thing you learned without looking.",
    ],
    weekly_additions: [
      null,
      "Turn off one non-essential notification.",
      "Check email at planned times, not continuously.",
      "Track how often you context-switch for one hour.",
      "Start with 10 minutes if 25 feels too long.",
      "Add 5 minutes to one focus block.",
      "Schedule focus before reactive work.",
      "Use headphones or a visual do-not-disturb cue.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold it for two weeks. Brain fog is one of the conditions that responds fastest to the basics — most people see real improvement in 2-3 weeks.",
    if_did_partial: "Halfway. Which one is hardest? The sleep is usually the highest-leverage; if that's the leg that's weak, address it first.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: clearer afternoons. Week 4: more consistent thinking through the day.",
    avoid: "Buying nootropic supplements before fixing the basics. Almost always a waste, and sometimes makes the underlying problem harder to spot.",
    red_flags: "Brain fog with new headaches, vision changes, balance issues, or memory problems — see a doctor. New cognitive symptoms in the absence of obvious cause warrant evaluation.",
    tracking_metric: "Days all three held",
    review_days: 14,
  },

  COGNITIVE_TRAINING: {
    category: 'brain_performance',
    difficulty: "easy",
    time_per_day: "15-30 min",
    coach_intro: "The brain stays sharp by being challenged — not by repeating what it already knows. Crosswords are fine; learning a new skill is better. We're going to add one deliberate cognitive challenge to your week.",
    this_week: "Pick one new thing to learn — language app, instrument, complex hobby, technical skill. 15-30 minutes daily. The newness is the point, not the duration.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 20–25 minute single-focus block with notifications off.",
      "Take a 10-minute walk or light break before your hardest thinking work.",
      "Practice active recall: summarize one thing you learned without looking.",
    ],
    weekly_additions: [
      null,
      "Repeat the same focus time tomorrow.",
      "Take a 10-minute walk before hard thinking.",
      "Use stairs or light movement to break brain fog.",
      "Walk outdoors when possible.",
      "Track focus before and after movement.",
      "Treat movement as brain prep, not just fitness.",
      "Protect a 7-hour sleep opportunity.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The neuroplasticity research is clear — novelty matters more than duration. Hold it. Switch the challenge every 8-12 weeks.",
    if_did_partial: "Halfway. Which days got missed? Often it's weekends, but learning on weekends is some of the best — relaxed brain, new things stick.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: the new skill is starting to take. Month 3: real progress. Month 6: a skill you didn't have before.",
    avoid: "'Brain training' apps that recycle the same patterns. Real learning beats them every time.",
    red_flags: "Memory issues that are new or worsening — that's a doctor's appointment, not a brain-training problem.",
    tracking_metric: "Learning days per week",
    review_days: 14,
  },

  COGNITIVE_ENGAGEMENT: {
    category: 'brain_performance',
    difficulty: "easy",
    time_per_day: "20-30 min",
    coach_intro: "An engaged mind ages slower than a passive one. Reading, conversation, learning, problem-solving — these aren't just 'nice.' They're maintenance. People who stay engaged stay sharp longer.",
    this_week: "30 minutes daily of active engagement — reading (real books), real conversation (not scrolling), or learning. Counts: book, podcast you actually think about, deep conversation. Doesn't count: passive video.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 20–25 minute single-focus block with notifications off.",
      "Take a 10-minute walk or light break before your hardest thinking work.",
      "Practice active recall: summarize one thing you learned without looking.",
    ],
    weekly_additions: [
      null,
      "Keep wake time steady.",
      "Phone charges away from bed.",
      "Dim lights before sleep.",
      "Notice whether sleep drives next-day focus.",
      "Learn one small thing for 10 minutes.",
      "Summarize from memory without looking.",
      "Teach the idea to someone or write it in plain English.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold the rhythm. Active engagement is one of the highest-return long-term investments in your brain.",
    if_did_partial: "Halfway. Which days were passive? Usually the tired ones — that's when scrolling wins. The fix: have the book or podcast queued and ready.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: more interesting conversations, more thoughts of your own. Month 2: a different relationship with your own mind.",
    avoid: "Treating passive consumption as engagement. There's a difference between watching a show and reading a book about the same topic. Both are fine; they're not the same.",
    red_flags: "Inability to focus on engagement that used to be rewarding — could be depression, exhaustion, or cognitive issue. Worth investigating.",
    tracking_metric: "Days with real engagement",
    review_days: 14,
  },

  DEEP_WORK_PROTOCOL: {
    category: 'brain_performance',
    difficulty: "moderate",
    time_per_day: "90-120 min",
    coach_intro: "Deep work — focused, demanding cognitive effort — is what produces the things you're proud of. It's also what's been quietly disappearing from most people's days, replaced by reactive shallow work. We're going to put it back on the schedule.",
    this_week: "One 90-minute deep work block daily, scheduled like a meeting. Same time each day if possible. Phone in another room. Single task — the most demanding thing you have.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 20–25 minute single-focus block with notifications off.",
      "Take a 10-minute walk or light break before your hardest thinking work.",
      "Practice active recall: summarize one thing you learned without looking.",
    ],
    weekly_additions: [
      null,
      "Review yesterday’s learning for 2 minutes.",
      "Use recall, not rereading, as the win.",
      "Read 5 pages or 10 minutes.",
      "Write one useful takeaway.",
      "Highlight less, summarize more.",
      "Turn one idea into an action.",
      "Review notes once at the end of the week.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. This is where real work happens. Hold the rhythm. After a month, you can extend or add a second block.",
    if_did_partial: "Halfway. The trap is meetings creeping in. The deep work block needs the same protection as a critical meeting — no exceptions for 'just a quick call.'",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: more sense of progress on important work. Week 4: less reactive, more directed days.",
    avoid: "Trying to do deep work on top of a full reactive day. It needs to come first — most days, before email.",
    red_flags: "Persistent inability to focus despite removing all external distractions — possible ADHD or other underlying issue worth investigating.",
    tracking_metric: "Deep work blocks per week",
    review_days: 7,
  },

  ENGAGEMENT_PROTOCOL: {
    category: 'brain_performance',
    difficulty: "easy",
    time_per_day: "30 min",
    coach_intro: "If you're feeling disengaged from work or life — going through motions but not really there — the fix usually isn't dramatic. It's small acts of engagement, repeated. Talk to someone real. Read something hard. Do one thing fully present.",
    this_week: "One real conversation daily (not transactional), one thing read or studied with full attention, and one fully-present meal. That's the protocol.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 20–25 minute single-focus block with notifications off.",
      "Take a 10-minute walk or light break before your hardest thinking work.",
      "Practice active recall: summarize one thing you learned without looking.",
    ],
    weekly_additions: [
      null,
      "Drink water and eat protein/fiber before assuming brain fog is cognitive.",
      "Take a 5-minute light break.",
      "Check sleep from the last two nights.",
      "Walk before caffeine if the fog appears mid-afternoon.",
      "Seek medical review if fog is new, severe, or persistent.",
      "Write tomorrow’s top 3 tasks tonight.",
      "Use one capture place for reminders.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Engagement is contagious — to yourself and others. Hold these and watch how others respond.",
    if_did_partial: "Halfway. Which slips most? Usually the screen-free meal. Small wins matter — just lunch.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: connections feel more meaningful. Week 4: more present in your own life.",
    avoid: "Forced 'mindfulness.' Engagement is just paying attention, not a meditation practice.",
    red_flags: "Persistent disengagement, lack of joy, feeling 'numb' — talk to a professional. Depression often presents this way.",
    tracking_metric: "Days you hit all three",
    review_days: 14,
  },

  FOCUS_BLOCKS: {
    category: 'brain_performance',
    difficulty: "easy",
    time_per_day: "60-90 min",
    coach_intro: "Focus doesn't happen in 5-minute increments — it requires runway. Once you've fragmented your attention enough times in a day, you can't focus deeply at all. The fix is structural: protected blocks, no exceptions.",
    this_week: "Two 60-minute focus blocks daily. Same times each day. Calendar-blocked. Phone away. Single task in each block.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 20–25 minute single-focus block with notifications off.",
      "Take a 10-minute walk or light break before your hardest thinking work.",
      "Practice active recall: summarize one thing you learned without looking.",
    ],
    weekly_additions: [
      null,
      "Do not rely on memory for errands.",
      "Make the next step visible.",
      "Review the list at the start of your workday.",
      "Have one real conversation without multitasking.",
      "Call someone instead of only texting once.",
      "Join one group, class, or community touchpoint.",
      "Ask one person a thoughtful question.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold the structure for 4 weeks before changing anything. The discipline becomes automatic.",
    if_did_partial: "Halfway. Usually it's the second block that breaks — afternoon meetings creep in. Anchor the morning block first; it's hardest to lose.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: more done in less time. Week 4: less anxiety about a 'busy day' because the important stuff happened first.",
    avoid: "Multitasking during the block. Single task or it's not focus — it's just busy.",
    red_flags: "Persistent inability to sustain focus on protected blocks, with other cognitive symptoms — see a doctor.",
    tracking_metric: "Blocks completed per week",
    review_days: 7,
  },

  FOCUS_ENVIRONMENT: {
    category: 'brain_performance',
    difficulty: "easy",
    time_per_day: "0 min — setup",
    coach_intro: "Your environment shapes your focus more than your willpower does. A cluttered desk and an open phone fight your attention all day. Fix the environment and focus is suddenly easier.",
    this_week: "Three changes: clean desk surface, phone in another room during work, and one tab/email window open at a time. Set up once, hold it all week.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 20–25 minute single-focus block with notifications off.",
      "Take a 10-minute walk or light break before your hardest thinking work.",
      "Practice active recall: summarize one thing you learned without looking.",
    ],
    weekly_additions: [
      null,
      "Use social contact as brain health, not only mood support.",
      "Schedule hearing or vision check if you notice changes.",
      "Do not ignore hearing loss in conversations.",
      "Use glasses/hearing aids consistently if prescribed.",
      "Reduce background noise for important conversations.",
      "Tell your clinician about new hearing or vision changes.",
      "Check BP if you have a cuff and risk factors.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The environment does the work — willpower drops with fatigue, but a clean desk doesn't. Hold it.",
    if_did_partial: "Halfway. Which slipped? Usually the phone — 'just for a minute' becomes the day. The fix: phone goes where you can't see it, not where you can.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 1: weird without the phone reflex. Week 2: more done. Week 4: phone-free hours feel normal.",
    avoid: "Checking notifications 'just to see.' That's the slot machine your brain learned to chase.",
    red_flags: "If phone-checking is compulsive and feels out of control, that's worth a conversation with a professional.",
    tracking_metric: "Days the environment held",
    review_days: 7,
  },

  LEARNING_PROTOCOL: {
    category: 'brain_performance',
    difficulty: "easy",
    time_per_day: "20-30 min",
    coach_intro: "Continuous learning is one of the highest-return long-term habits there is. People who keep learning stay sharp, adaptive, and engaged for decades longer. The trick is making it a daily habit, not a sporadic project.",
    this_week: "20-30 minutes daily of deliberate learning. Same time each day if possible. Could be a course, a book, a language, a skill — your choice. Just consistent.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 20–25 minute single-focus block with notifications off.",
      "Take a 10-minute walk or light break before your hardest thinking work.",
      "Practice active recall: summarize one thing you learned without looking.",
    ],
    weekly_additions: [
      null,
      "Track readings properly, not randomly.",
      "Share patterns with your clinician.",
      "Walk and reduce sodium as brain-health supports.",
      "Do not ignore high readings.",
      "Add one protein + fiber meal.",
      "Choose fish, olive oil, nuts, beans, greens, or berries once.",
      "Reduce a sugary drink once.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The cumulative effect of this is real — most adults don't have a deliberate learning habit. You'll be in the top 10% within a month.",
    if_did_partial: "Halfway. Which days got missed? Usually busy days. The fix is making the session shorter on those days, not skipping entirely. 5 minutes counts.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Month 1: real progress on the chosen skill. Month 3: visible competence. Month 12: a different person.",
    avoid: "Topic-hopping every two weeks. Pick one, stay 8+ weeks.",
    red_flags: "If learning that used to be enjoyable now feels impossible — could be depression, exhaustion, or cognitive issue.",
    tracking_metric: "Learning days per week",
    review_days: 14,
  },

  SINGLE_FOCUS: {
    category: 'brain_performance',
    difficulty: "easy",
    time_per_day: "In-the-moment discipline",
    coach_intro: "Multitasking isn't real — your brain is just rapid-switching, and every switch is a tax. The way to do more is paradoxically to do one thing at a time, fully. We're going to practice this in small windows.",
    this_week: "Three 30-minute single-task windows daily. During those windows: one task, one tab, one document, no notifications. Do them at the same times daily.",
    coach_story: null,
    daily_micro_wins: [
      "Do one 20–25 minute single-focus block with notifications off.",
      "Take a 10-minute walk or light break before your hardest thinking work.",
      "Practice active recall: summarize one thing you learned without looking.",
    ],
    weekly_additions: [
      null,
      "Do not work through hunger and call it focus.",
      "Track whether lunch improves or hurts afternoon focus.",
      "Spend 10 minutes making something.",
      "Write before consuming content.",
      "Keep one idea notebook.",
      "Use a bad first draft as the goal.",
      "Do not judge creative reps by quality.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The discipline of single-tasking is a real skill — and it's becoming rare. Hold it.",
    if_did_partial: "Halfway. The trap is 'just checking' something quickly during the window. Even a 30-second check costs you 5 minutes of recovery focus.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: less mental fatigue at end of day. Week 4: more done with less effort.",
    avoid: "'I work better with multiple tabs open.' The research is unanimous — you don't.",
    red_flags: "Inability to maintain single focus despite a clean environment — possible attention issue worth evaluating.",
    tracking_metric: "Single-task windows per week",
    review_days: 7,
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  LONGEVITY & PREVENTION
  // ─────────────────────────────────────────────────────────────────────────

  HABIT_BUILDING: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "5 min",
    coach_intro: "I've coached wrestlers for 40 years. The athletes who win aren't the most talented — they're the ones who built reliable habits when nobody was watching. Habits aren't motivation; they're a system. We're going to build one.",
    this_week: "Pick ONE habit you want. Just one. Anchor it to something you already do daily — coffee, brushing teeth, lunch. Same time, same trigger, every day.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Choose one action that helps you feel better this year, not just live longer.",
      "Track energy, strength, sleep, and connection as healthspan signs.",
      "Pick the smallest useful habit today.",
      "Do not chase every longevity trend at once.",
      "Ask: will this help me be stronger, sharper, or more independent?",
      "Schedule your overdue primary-care visit.",
      "Write 3 questions before the appointment.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. By week 4 it'll feel automatic. Then — and only then — add a second habit. Most people fail because they pile on.",
    if_did_partial: "Halfway. Which days got skipped? The fix is usually the anchor — if the anchor wasn't reliable, neither was the habit. Pick a stronger anchor.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: the habit starts feeling natural. Week 4: missing it feels weird. Week 8: automatic.",
    avoid: "Building five habits at once. Even three is too many. One.",
    red_flags: "If habits never stick despite small starts, it might be worth examining whether something deeper (depression, ADHD) is making consistency hard.",
    tracking_metric: "Days the habit happened",
    review_days: 7,
  },

  HABIT_SYSTEM: {
    category: 'longevity_prevention',
    difficulty: "moderate",
    time_per_day: "10-15 min",
    coach_intro: "If you've already got a habit or two locked in, the next move is a system — habits stacked together so they reinforce each other. Morning routine, evening wind-down, weekend reset. Systems do the work for you.",
    this_week: "Build one of three: morning routine, evening wind-down, or weekend planning. Pick the one that'll have the biggest leverage. Three habits stacked, in order, every day.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Bring medication and supplement list.",
      "Ask which screenings fit your age, sex, family history, and risks.",
      "Put the follow-up step on your calendar before leaving.",
      "Take BP seated with proper technique if you have a cuff.",
      "Record two readings one minute apart.",
      "Bring readings to your clinician.",
      "Use walking, sleep, and sodium reduction as BP-support habits.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Systems compound — each habit makes the next easier. Hold this for 8 weeks before adding another routine.",
    if_did_partial: "Halfway. Where in the sequence does it break? Usually the middle habit, because the first one is anchored and the third one's reward is too far. Tighten the middle.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: the routine feels like 'who I am' rather than 'what I'm trying to do.' That's the goal.",
    avoid: "Five-step morning routines that take an hour. Three habits, 15 minutes max — sustainable.",
    red_flags: "If routines never stick, look upstream — sleep, mood, or stress may be eating the consistency.",
    tracking_metric: "Days the routine completed in full",
    review_days: 14,
  },

  ACCOUNTABILITY_PLAN: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "5 min",
    coach_intro: "Most goals fail in private. The fastest way to make a goal more likely to happen is to put a witness on it — someone who'll notice if you don't. That's what coaches do. We're going to build a tiny version of that for you.",
    this_week: "Pick one person — partner, friend, coach, app. Tell them this week's goal. Send them a yes/no update every day. That's the system.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Know emergency BP symptoms and act immediately.",
      "Find your latest labs and note what is missing.",
      "Ask your clinician about lipids, A1c/glucose, kidney/liver markers, and inflammation only when appropriate.",
      "Do not interpret labs in isolation.",
      "Track trends over time.",
      "Make one follow-up appointment if labs are abnormal.",
      "Check which cancer screenings apply to you.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The accountability is doing the work. Hold the partner; rotate the goal weekly if you want.",
    if_did_partial: "Halfway. Which days got skipped — usually the 'no' days, because they feel embarrassing. That's exactly when you need to send it.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: more consistency than you've had in months on this kind of goal.",
    avoid: "Picking an accountability partner who'll let you off the hook. That's not accountability; that's permission.",
    red_flags: "If accountability triggers shame spirals, that's worth talking to a professional — perfectionism patterns can sabotage progress.",
    tracking_metric: "Daily updates sent",
    review_days: 7,
  },

  MOTIVATION_BUILDING: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "10 min",
    coach_intro: "Motivation is unreliable — it shows up some days, not others. So we don't build on motivation. We build on systems. But there's also a real role for clarity about why this matters to you. That's what we're working on this week.",
    this_week: "30 minutes, alone, writing: Why do I want to make this change? Not the surface answer — the real one. Read it once a day this week.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Schedule one overdue screening conversation.",
      "Ask how family history changes timing.",
      "Do not use generic app guidance as a diagnosis.",
      "Celebrate scheduling as the win.",
      "Review vaccine status with your clinician/pharmacy.",
      "Check flu/COVID/tetanus/shingles/pneumonia eligibility as appropriate.",
      "Put next vaccine date on calendar.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The 'why' is your anchor for the days motivation is gone. Hold the practice.",
    if_did_partial: "Halfway. The reading often gets skipped on the days you most need it — that's when motivation is lowest.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: easier to start on hard days. Week 4: the 'why' becomes part of how you make decisions.",
    avoid: "A surface-level 'why' that doesn't actually move you. If your written reason doesn't make you feel something when you read it, dig deeper.",
    red_flags: "Persistent inability to find a 'why' worth pursuing — that's a possible depression signal worth talking to a professional about.",
    tracking_metric: "Days you read your why",
    review_days: 14,
  },

  FIND_YOUR_WHY: {
    category: 'longevity_prevention',
    difficulty: "moderate",
    time_per_day: "30 min reflection",
    coach_intro: "A why that's just 'lose weight' or 'get healthier' isn't actually a why — it's a goal. The why is what's behind the goal. Why do you want to lose weight? To play with your grandkids? To not be the parent watching from the sidelines? That's the why. The clearer it gets, the more it pulls you forward.",
    this_week: "30 minutes, paper and pen, alone. Ask: Why does this matter? When the answer comes, ask why again. Five 'whys' deep — that's where the real one usually lives.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Ask about travel vaccines before travel.",
      "Use vaccines as prevention, not politics.",
      "Schedule dental cleaning if overdue.",
      "Schedule eye exam if overdue.",
      "Ask for hearing check if conversations are harder.",
      "Use prescribed glasses/hearing aids consistently.",
      "Treat sensory health as brain and safety health.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Read what you wrote each Sunday morning — that's the practice. The clearer the why, the easier the consistency.",
    if_did_partial: "Even 15 minutes is real. What's blocking the rest? Sometimes the 'why' is uncomfortable to look at directly.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: harder to talk yourself out of the work. Week 4: a different relationship with the goal.",
    avoid: "A why that's about other people's expectations. If it's not yours, it won't sustain you.",
    red_flags: "If reflection brings up overwhelming emotions, that's worth processing with a therapist — not a sign to stop, just a sign to get support.",
    tracking_metric: "Reflection completed",
    review_days: 14,
  },

  BUILD_CONNECTIONS: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "15-30 min",
    coach_intro: "Loneliness isn't just sad — it's a measurable health risk on par with smoking. Strong relationships are one of the highest-return investments in long-term health. We're going to make connection a deliberate habit.",
    this_week: "Three real conversations this week. Not text, not email — voice or in-person. Could be 5 minutes or an hour. Three.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Do one set of sit-to-stands.",
      "Schedule two strength sessions this week.",
      "Train grip, legs, push, pull, and carry over time.",
      "Progress slowly and safely.",
      "Track strength as independence insurance.",
      "Walk 10 minutes today.",
      "Build toward 150 minutes/week gradually.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Three is the floor, not the ceiling. Hold the rhythm. Loneliness shrinks the more you reach out.",
    if_did_partial: "Halfway. The reach-out is usually the missing one. People love hearing from someone unexpectedly. The friction is your hesitation, not theirs.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: more sense of belonging. Month 2: a meaningfully different social life.",
    avoid: "Substituting social media for real connection. They activate different parts of the brain.",
    red_flags: "Persistent isolation despite efforts to connect — could be depression. Worth talking to a professional.",
    tracking_metric: "Real conversations per week",
    review_days: 7,
  },

  COMMUNITY_BUILDING: {
    category: 'longevity_prevention',
    difficulty: "moderate",
    time_per_day: "1-2 hr/week",
    coach_intro: "A community is bigger than a few friends — it's a regular gathering where you have a place. Religious community, volunteer group, hobby club, gym, recurring meetup. People who belong somewhere live longer and happier.",
    this_week: "Identify one community to invest in. Could be one you're already loosely part of, or a new one to try. Show up to one gathering this week.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Use conversational pace as the base.",
      "Add hills or intervals only after consistency.",
      "Track weekly minutes.",
      "Practice balance near support for 20 seconds.",
      "Clear one tripping hazard at home.",
      "Use good lighting at night.",
      "Train legs with sit-to-stands.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Show up next week too. Community takes consistent presence to develop, not single visits.",
    if_did_partial: "Showing up once is hard. The next time is easier — pre-decide it now.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 4: starting to know people by name. Month 3: a place where people are glad to see you.",
    avoid: "Trying to build community without consistent presence. Sporadic doesn't work.",
    red_flags: "Severe social anxiety that prevents trying — talk to a professional. It's treatable.",
    tracking_metric: "Community gatherings attended",
    review_days: 14,
  },

  WEEKLY_PLAN: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "15 min Sunday",
    coach_intro: "Sunday planning is the highest-leverage 15 minutes of your week. Pick three priorities, block your calendar, identify the obstacles. The week runs you instead of running you.",
    this_week: "Sunday evening, 15 minutes: three priorities for the week, calendar blocks for the most important work, and one anticipated obstacle and how you'll handle it.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Discuss falls, dizziness, or neuropathy with a clinician.",
      "Add protein to one meal.",
      "Pair protein with strength training days.",
      "Use whole-food protein when possible.",
      "Do not diet away muscle.",
      "Ask a clinician/dietitian if kidney disease or other issues affect protein needs.",
      "Add beans, vegetables, fruit, oats, or whole grains today.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold this every week. The 15-minute investment pays back hours.",
    if_did_partial: "If you only have 5 minutes, that's enough — just pick the three priorities. Skip the calendar piece if needed.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: less reactive weeks. Week 4: more sense of progress.",
    avoid: "Over-planning. The point is to pick three things, not to map every hour.",
    red_flags: "If planning triggers excessive anxiety, that's a perfectionism pattern worth examining.",
    tracking_metric: "Sundays you planned",
    review_days: 7,
  },

  DETAILED_PLAN: {
    category: 'longevity_prevention',
    difficulty: "moderate",
    time_per_day: "30 min",
    coach_intro: "If you've got a major goal — losing 30 lbs, training for an event, recovering from an illness — vague plans don't cut it. You need a real plan with milestones, expected obstacles, and contingencies. The detail is the discipline.",
    this_week: "Write a real plan: the goal, the timeline, three milestones with dates, the daily/weekly habits required, the obstacles you anticipate, and your responses to each obstacle.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Choose protein + fiber before refined carbs.",
      "Walk after one meal.",
      "Reduce one added-sugar item.",
      "Track waist or weight trend if appropriate.",
      "Give yourself 7+ hours sleep opportunity.",
      "Keep wake time stable.",
      "Screen for apnea if snoring plus sleepiness.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Read the plan every Sunday. Adjust monthly. The plan is alive.",
    if_did_partial: "Even half a plan is real. Finish next week. The detail-work is what separates intention from execution.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: more clarity on what to actually do daily. Week 4: real progress visible.",
    avoid: "Plans so detailed they become projects in themselves. The plan should fit on one page.",
    red_flags: "If planning becomes a substitute for action, that's a procrastination pattern — set a do-by date for the plan itself.",
    tracking_metric: "Plan completed + adherence",
    review_days: 14,
  },

  CHECKLIST_PLAN: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "5 min",
    coach_intro: "Sometimes the answer isn't more motivation — it's a checklist. Pilots use them, surgeons use them, coaches use them. Same actions, every time, regardless of how you feel. Checklists beat memory and willpower.",
    this_week: "Build one checklist for one routine: morning, pre-workout, evening, whatever. 3-5 items, in order. Use it every day this week.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Move caffeine earlier.",
      "Treat sleep as repair time.",
      "Text or call one person.",
      "Schedule a walk with someone.",
      "Join one group or recurring activity.",
      "Ask for accountability on one habit.",
      "Do not let health become a solo project.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Hold it. After 4 weeks, the checklist becomes unnecessary — it's automatic.",
    if_did_partial: "Halfway. Where did it break? Usually one item is the 'I'll skip this one' item. Either remove it or commit to it.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: routines feel automatic. Week 4: things you used to forget consistently happen.",
    avoid: "Perfectionism, all-or-nothing thinking. Half a checklist is better than no checklist.",
    red_flags: "If checklists become anxiety-producing, that's a perfectionism signal worth working on.",
    tracking_metric: "Checklist completion %",
    review_days: 14,
  },

  SIMPLE_TRACKING: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "1 min",
    coach_intro: "What gets measured gets managed. But you don't need an app or a spreadsheet. A simple yes/no on a paper calendar does the same job, with less friction. We're tracking one thing this week.",
    this_week: "Pick one habit. Track it daily with a simple yes/no — paper, phone notes, calendar X. One minute per day max.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Track nicotine exposure honestly.",
      "Ask your clinician about cessation tools.",
      "Make one smoke-free space.",
      "Delay first nicotine use by 10 minutes if quitting feels too big.",
      "Do not rely on shame; use support.",
      "Choose one alcohol-free night.",
      "Track sleep after drinking versus not drinking.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The marking creates pressure not to break the streak. Hold it 4 weeks; consider adding a second habit then.",
    if_did_partial: "Halfway. The forgetting is the issue, not the doing. Move the tracker to where you'll see it.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: more consistent than you expected just because you're watching. Week 4: a chain you don't want to break.",
    avoid: "Apps that gamify so heavily that you spend more time on the app than the habit.",
    red_flags: "If tracking becomes obsessive or anxiety-inducing, drop it. Awareness, not pressure, is the goal.",
    tracking_metric: "Days marked",
    review_days: 7,
  },

  START_TRACKING: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "2 min",
    coach_intro: "If you've never tracked anything, the resistance is usually 'I don't want to be that kind of person.' Fair. But you don't have to be. Two minutes a day, one habit, on paper. That's all.",
    this_week: "Pick the habit. Pick the medium (paper calendar, phone note, sticky note). Mark the first day today.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Drink water between alcoholic drinks if you drink.",
      "Stop alcohol several hours before bed.",
      "Ask for support if alcohol feels hard to control.",
      "Update your medication and supplement list.",
      "Check interactions with clinician/pharmacist.",
      "Do not add multiple supplements at once.",
      "Track why you take each supplement.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The streak is now your motivation. Hold it.",
    if_did_partial: "Even 4 days marked is real. Same habit next week.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Week 2: notice yourself thinking about the habit at marking time.",
    avoid: "Apps with notifications that become noise.",
    red_flags: "If tracking ANY habit feels impossible, it's worth examining whether something deeper is at play.",
    tracking_metric: "Days marked",
    review_days: 7,
  },

  PREVENTION_PROTOCOL: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "Variable",
    coach_intro: "The cheapest health intervention there is is the one that catches a problem early. Annual physicals, age-appropriate screenings, eye and dental check-ups — these aren't optional. They're the basic maintenance that lets you keep going.",
    this_week: "Audit: when was your last physical? Last bloodwork? Last dental visit? Last eye exam? Schedule any that are overdue. One call this week.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Stop guessing when symptoms or labs are involved.",
      "Use sun protection during outdoor time.",
      "Check new or changing skin spots.",
      "Schedule dermatology if high risk or concerning changes.",
      "Balance sunlight benefits with burn prevention.",
      "Do not ignore bleeding/changing lesions.",
      "Pick one healthspan habit for this week.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Show up. Make a calendar reminder for next year while you're there.",
    if_did_partial: "What's blocking it? Cost, time, fear of what they'll find? Each has a workaround. Tell me which.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "The peace of mind of knowing where you stand is its own benefit.",
    avoid: "Putting off appointments because you 'feel fine.' That's exactly when prevention matters — before symptoms.",
    red_flags: "Skipping screenings because of fear is a sign to talk to a doctor or therapist. Avoidance compounds.",
    tracking_metric: "Appointments scheduled",
    review_days: 7,
  },

  SCHEDULE_CHECKUP: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "15 min",
    coach_intro: "If it's been more than a year since your last physical, this week is for the call. No drama. Just the call.",
    this_week: "Call your primary care doctor. Schedule a physical. Request bloodwork (CBC, metabolic panel, lipid panel, A1c, vitamin D, thyroid). That's it.",
    coach_story: null,
    daily_micro_wins: [
      "Schedule or complete the medical follow-up that has been delayed.",
      "Write your symptoms, medications, and questions before the appointment.",
      "Bring your last BP/labs/vitals if available.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Show up. Bring questions. Get copies of your bloodwork.",
    if_did_partial: "Half a call is no call. Try a portal message instead — easier than the phone.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Most people feel better just having the appointment on the calendar.",
    avoid: "Asking for everything except bloodwork. Bloodwork is the highest-information piece of a checkup.",
    red_flags: "If you're avoiding the doctor because of specific symptoms, those symptoms are exactly why you need to go.",
    tracking_metric: "Appointment scheduled",
    review_days: 7,
  },

  MEDICAL_CHECK: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "Appointment time",
    coach_intro: "Some symptoms warrant a doctor's eye, even if they're not urgent. We're going to get this on the calendar so it doesn't drift.",
    this_week: "Call or message your doctor. Describe the symptom. Ask if you should come in. Don't self-diagnose; get their read.",
    coach_story: null,
    daily_micro_wins: [
      "Schedule or complete the medical follow-up that has been delayed.",
      "Write your symptoms, medications, and questions before the appointment.",
      "Bring your last BP/labs/vitals if available.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Show up. Ask questions. Bring the symptom log.",
    if_did_partial: "Half-step counts. Send the portal message tomorrow.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Most non-urgent symptoms have simple answers. The visit is usually the hardest part.",
    avoid: "Googling instead of calling. Google amplifies anxiety; doctors give you context.",
    red_flags: "Worsening symptoms while you wait for an appointment — call back; sometimes you need to be moved up.",
    tracking_metric: "Visit scheduled",
    review_days: 7,
  },

  MEDICAL_REMINDER: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "5 min",
    coach_intro: "If you've already been told by a doctor to follow up on something — bloodwork, a referral, a specialist visit — and it hasn't happened yet, this week is the week.",
    this_week: "Look up the unfinished medical thing. Schedule it. Block it in your calendar. Done.",
    coach_story: null,
    daily_micro_wins: [
      "Schedule or complete the medical follow-up that has been delayed.",
      "Write your symptoms, medications, and questions before the appointment.",
      "Bring your last BP/labs/vitals if available.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. The hard part is done.",
    if_did_partial: "Send the portal message — easier than the call.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Relief.",
    avoid: "Telling yourself 'it's probably nothing.' That's a guess; the doctor's appointment is information.",
    red_flags: "If you're avoiding because of fear of what they'll find, that's worth talking through with a therapist.",
    tracking_metric: "Outstanding item scheduled",
    review_days: 7,
  },

  MONITOR_SYMPTOMS: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "2 min",
    coach_intro: "When you've got a symptom that comes and goes, the most useful thing you can do for your doctor is track it. Patterns that aren't visible day-to-day become obvious in a log.",
    this_week: "Track the symptom daily — when, severity 1-10, what you were doing, what you ate, how you slept. Phone notes, paper, your choice. Two minutes a day.",
    coach_story: null,
    daily_micro_wins: [
      "Choose one preventive task: appointment, lab review, dental, vision, vaccine, or BP check.",
      "Do one healthspan foundation today: walk, strength, protein/fiber meal, or sleep anchor.",
      "Make the next step visible on your calendar or checklist.",
    ],
    weekly_additions: [
      null,
      "Bring readings to your clinician.",
      "Use walking, sleep, and sodium reduction as BP-support habits.",
      "Know emergency BP symptoms and act immediately.",
      "Find your latest labs and note what is missing.",
      "Ask your clinician about lipids, A1c/glucose, kidney/liver markers, and inflammation only when appropriate.",
      "Do not interpret labs in isolation.",
      "Track trends over time.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Patterns emerge in 2-4 weeks. The log is more useful to your doctor than your memory.",
    if_did_partial: "Even half the days helps. Don't try to reconstruct — start fresh tomorrow.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Patterns you didn't see — symptoms tied to specific foods, sleep, stress, time of day.",
    avoid: "Diagnosing yourself based on the log. The log is data; interpretation is the doctor's job.",
    red_flags: "Worsening symptoms — don't wait to log; call the doctor.",
    tracking_metric: "Days logged",
    review_days: 14,
  },

  BP_MONITORING: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "2 min",
    coach_intro: "If you've been told your blood pressure is borderline or high, home monitoring is the highest-leverage thing you can do. Two minutes daily, a $40 cuff, and you have data your doctor can actually act on.",
    this_week: "Get a home BP cuff (any drugstore or Amazon). Take your blood pressure at the same time daily — morning, before coffee. Log it.",
    coach_story: null,
    daily_micro_wins: [
      "Take two seated BP readings using proper technique and write them down.",
      "Share abnormal patterns with your healthcare team instead of self-diagnosing.",
      "Do one BP-supporting action: walk, low-sodium swap, or sleep window protected.",
    ],
    weekly_additions: [
      null,
      "Make one follow-up appointment if labs are abnormal.",
      "Check which cancer screenings apply to you.",
      "Schedule one overdue screening conversation.",
      "Ask how family history changes timing.",
      "Do not use generic app guidance as a diagnosis.",
      "Celebrate scheduling as the win.",
      "Review vaccine status with your clinician/pharmacy.",
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Bring the log to your next doctor visit. Most doctors love patients who track.",
    if_did_partial: "Half the readings still helps. Same time daily is the lever — pick a time that's anchored to something else.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Patterns — morning vs. evening, work-stress days vs. relaxed days.",
    avoid: "Taking BP after coffee, exercise, or stress. The reading reflects the moment, not the baseline.",
    red_flags: "Readings consistently above 140/90, or any reading above 180/120 — call your doctor; the latter is urgent.",
    tracking_metric: "Days BP logged",
    review_days: 7,
  },

  BP_CHECK: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "15 min",
    coach_intro: "If you don't know your blood pressure and you're over 30, it's time to find out. High blood pressure is silent — most people who have it don't know — and it's also one of the most treatable things in medicine.",
    this_week: "Get your blood pressure checked. Drugstore kiosk, doctor's office, or buy a home cuff. Log the number.",
    coach_story: null,
    daily_micro_wins: [
      "Take two seated BP readings using proper technique and write them down.",
      "Share abnormal patterns with your healthcare team instead of self-diagnosing.",
      "Do one BP-supporting action: walk, low-sodium swap, or sleep window protected.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Now know your number. If it's normal, recheck yearly. If borderline or high, monitor more often and follow up with a doctor.",
    if_did_partial: "Half-step. The drugstore kiosk is fastest — 5 minutes.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "If it's normal: peace of mind. If it's not: you've caught it early.",
    avoid: "Assuming you're fine because you 'feel fine.' BP is silent.",
    red_flags: "Readings above 140/90 — see a doctor. Above 180/120 — call your doctor today.",
    tracking_metric: "Reading obtained",
    review_days: 7,
  },

  MEDICAL_EVAL: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "15 min",
    coach_intro: "Some symptoms warrant a doctor's evaluation, period. Don't try to coach your way through it. Get evaluated. The visit is information, and information is power.",
    this_week: "Make the appointment. Describe the symptoms. Ask for the evaluation. That's it.",
    coach_story: null,
    daily_micro_wins: [
      "Schedule or complete the medical follow-up that has been delayed.",
      "Write your symptoms, medications, and questions before the appointment.",
      "Bring your last BP/labs/vitals if available.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Show up. Bring the symptom log. Ask questions.",
    if_did_partial: "Send the portal message tonight. 'I have these symptoms; I need to be evaluated.' One sentence is enough.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Most evaluations have simple, treatable answers.",
    avoid: "Self-diagnosing on Google. Doctors give context Google can't.",
    red_flags: "Worsening symptoms while you wait — call back and ask to be moved up. Sometimes you need to be seen sooner.",
    tracking_metric: "Appointment scheduled",
    review_days: 7,
    escalation: "Don't wait to see if symptoms 'go away on their own.' If a doctor's evaluation is on the table, get it.",
  },

  URGENT_CHECKUP: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "Same day",
    coach_intro: "Some things can't wait for next week's appointment. We're going to get you in this week — same day if possible.",
    this_week: "Call your doctor today. If they can't see you this week, urgent care or telehealth is fine. The point is to be seen now, not later.",
    coach_story: null,
    daily_micro_wins: [
      "Schedule or complete the medical follow-up that has been delayed.",
      "Write your symptoms, medications, and questions before the appointment.",
      "Bring your last BP/labs/vitals if available.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Follow whatever they recommended. Don't talk yourself out of follow-up steps.",
    if_did_partial: "Half-step. Telehealth is the fastest option if you can't get to the office.",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "Most urgent symptoms have answers — and the answer arrives faster the sooner you go.",
    avoid: "'It'll probably get better on its own.' For urgent symptoms, that's a gamble that doesn't favor you.",
    red_flags: "Worsening symptoms — go to the ER, not next week's appointment.",
    tracking_metric: "Evaluation completed",
    review_days: 3,
    escalation: "If symptoms include chest pain, severe shortness of breath, sudden severe headache, weakness on one side, or confusion — call 911. Don't drive yourself.",
  },

  URGENT_MEDICAL_EVAL: {
    category: 'longevity_prevention',
    difficulty: "easy",
    time_per_day: "Same day",
    coach_intro: "Some symptoms shouldn't wait. This is one of those.",
    this_week: "Today. Call your doctor or go to urgent care or the ER, depending on severity. Don't talk yourself out of it.",
    coach_story: null,
    daily_micro_wins: [
      "Schedule or complete the medical follow-up that has been delayed.",
      "Write your symptoms, medications, and questions before the appointment.",
      "Bring your last BP/labs/vitals if available.",
    ],
    weekly_additions: [
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    ],
    weekly_check_in: "How many days did you complete the 3 core actions, and which one got in the way?",
    if_did_well: "Good. Follow their recommendations.",
    if_did_partial: "If you didn't go, please go today. Don't 'see how it feels tomorrow.'",
    if_did_zero: "No judgment. That tells me the step was too big, the timing was wrong, or life got in the way. This week, pick just one action and do it once. Can you do that?",
    what_youll_notice: "The peace of mind of knowing.",
    avoid: "Waiting and hoping.",
    red_flags: "Chest pain, severe shortness of breath, sudden severe headache, vision changes, weakness on one side of the body, confusion, fainting — go to the ER or call 911.",
    tracking_metric: "Evaluation completed",
    review_days: 1,
    escalation: "If you're having symptoms that could indicate heart attack, stroke, or other emergency — call 911 immediately. Don't drive yourself.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  SKIN HEALTH & HEALTHY AGING (added May 2026)
  // ─────────────────────────────────────────────────────────────────────────

  SKIN_PROTOCOL: {
    category: 'skin_health',
    theme: 'Protect & Repair Your Skin',
    difficulty: "easy",
    time_per_day: "3-5 min",
    coach_intro: "Skin health isn't vanity. The sun damage you take today is what shows up — wrinkled, spotted, sometimes cancerous — twenty years from now. The good news: the basics are simple and they actually work. We protect first, repair second, and we always check the spots that worry us.",
    this_week: "Build a simple skin routine: gentle cleanse and moisturize, morning and night. Apply broad-spectrum SPF 30+ to face, neck, ears, and hands before you leave the house — keep the sunscreen on your bathroom counter so you can't miss it. Cover up with a hat and long sleeves for sustained outdoor time. If you have a spot that's new, changing, bleeding, or not healing, take a photo this week and book a dermatologist.",
    daily_micro_wins: [
      "Cleanse and moisturize morning and night — a gentle routine beats expensive serums.",
      "Apply broad-spectrum SPF 30+ to face, neck, ears, and hands before you go outside.",
      "Cut one source of added sugar today — sugar drives glycation, one of the bigger skin-aging accelerators.",
    ],
    weekly_additions: [
      null,
      "Wear a hat and long-sleeve shirt for any outdoor time over 30 minutes.",
      "Do a 5-minute skin self-check using the ABCDE rule (asymmetry, border, color, diameter, evolving).",
      "Add fatty fish or omega-3 plant foods twice this week.",
      "Photograph any spot you're watching so you can compare it later.",
      "Add a colorful plant food (berries, greens, peppers) to one meal a day.",
      "Try a fragrance-free moisturizer if your skin has been dry or irritated.",
      "Book a dermatology check if anything has changed or won't heal.",
    ],
    weekly_check_in: "Days you wore SPF, and any spots you noticed or photographed.",
    if_did_well: "Good. Sunscreen is the boring thing that beats every expensive serum. Keep it visible — that's how it stays a habit.",
    if_did_partial: "Skin habits are sneaky — easy to skip on cloudy days. Stack SPF onto something you already do every morning. Brush teeth, then SPF. Don't overthink it.",
    if_did_zero: "No judgment. Sunscreen feels optional until you see the damage. Pick one trigger — leaving the house, going outside, getting in the car — and do it once this week.",
    what_youll_notice: "Week 1: it becomes automatic. Week 4: dryness and irritation start to settle. Long term: this is the single best thing you can do for how your skin ages.",
    avoid: "Tanning beds. Treating a worrying spot with cosmetic products before it's evaluated. Stacking five new serums in one week — your skin doesn't like chaos.",
    red_flags: "A spot that's new, changing, asymmetric, multi-colored, larger than a pencil eraser, bleeding, itching, or not healing needs a dermatologist — not a guess. Same for any non-healing sore.",
    tracking_metric: "Days SPF applied / spots photographed",
    review_days: 7,
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  STRENGTH, MUSCLE & FUNCTION (added May 2026)
  // ─────────────────────────────────────────────────────────────────────────

  STRENGTH_PROTOCOL: {
    category: 'strength_function',
    theme: 'Stay Strong as You Age',
    difficulty: "moderate",
    time_per_day: "10-15 min",
    coach_intro: "Strength isn't about looking strong. It's about being able to do your life — get up off the floor, carry the groceries, climb the stairs, play with the grandkids. Muscle is the organ of longevity. We build it slowly, with real loads, and we eat enough protein to keep it.",
    this_week: "Two sit-to-stand sessions: 10 reps from a sturdy chair, no hands if you can, hands if you need them. Add a palm-sized protein at breakfast every day. That's the assignment.",
    daily_micro_wins: [
      "Eat a palm-sized protein serving at breakfast.",
      "Do 10 sit-to-stand reps from a chair (no hands if you can).",
      "Walk for 10 continuous minutes — pace doesn't matter yet.",
    ],
    weekly_additions: [
      null,
      "Add one more sit-to-stand session this week (now 3 total).",
      "Carry a heavy bag or two grocery bags for 1-2 minutes a day.",
      "Practice single-leg balance near a counter for 30 seconds each side.",
      "Add a second strength session this week — bands, dumbbells, or bodyweight.",
      "Train grip: hang from a sturdy bar or squeeze a tennis ball for 30 seconds.",
      "Stretch hips, calves, chest, and shoulders for 5 minutes after walks.",
      "Increase one exercise by 1-2 reps or a slightly heavier load.",
    ],
    weekly_check_in: "Strength sessions completed, and how protein hit at each meal.",
    if_did_well: "Real progress. Strength is built one rep, one meal at a time — and you just stacked seven days. Next week we add one new pattern: carry, balance, or grip.",
    if_did_partial: "That's still movement. Same target next week. Tell me what got in the way — usually it's the chair you used, the time of day, or just not having the protein in the house.",
    if_did_zero: "No judgment. The plan was too big. This week: 5 sit-to-stands, once. That's the whole assignment.",
    what_youll_notice: "Week 2: easier getting up from chairs. Week 4: stairs feel less like a stair-climb. Week 8: you'll notice you're carrying things without thinking about it.",
    avoid: "Skipping protein. Testosterone boosters, SARMs, hormone stacks, extreme pre-workouts. Training through chest pain, dizziness, or sharp joint pain.",
    red_flags: "Chest pain, severe shortness of breath, dizziness, fainting, or sharp joint pain during exercise — stop and seek care. Recurrent falls or sudden weakness on one side need urgent evaluation.",
    tracking_metric: "Strength sessions per week / protein meals hit",
    review_days: 7,
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  DIGESTIVE HEALTH & MICROBIOME (added May 2026)
  // ─────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────
  //  HORMONE HEALTH & VITALITY (added May 2026)
  // ─────────────────────────────────────────────────────────────────────────

  HORMONE_PROTOCOL: {
    category: 'hormone_health_vitality',
    theme: 'Prepare for a Clinician Conversation',
    difficulty: "moderate",
    time_per_day: "10 min",
    coach_intro: "Hormone health is a full-body topic — sleep, stress, body composition, vascular health, medications, and labs all play in. We don't diagnose hormone problems from a quiz. What we do is help you track patterns and prepare a better conversation with a qualified clinician. The lifestyle work below is safe, useful, and almost always overlaps with what the clinician will recommend first.",
    coach_intro_male: "Hormone health for men is mostly testosterone, thyroid, sleep, body composition, and vascular health — and they all feed each other. We don't diagnose anything from a quiz. What we do is track patterns and prepare you for a smart conversation with a clinician. The lifestyle work below — sleep, strength, protein, walking — is what any good doctor will recommend first anyway, and it moves testosterone, energy, and libido more than most people expect.",
    coach_intro_female: "Hormone health for women shifts across decades — cycles, perimenopause, menopause, thyroid, stress, sleep, body composition — and they all interact. We don't diagnose anything from a quiz. What we do is track patterns and prepare you for a smart conversation with a menopause- or hormone-informed clinician. The lifestyle work below — sleep, strength, protein, walking — overlaps with what a good clinician will recommend first.",
    this_week: "Start a 14-day symptom log: sleep, energy, mood, temperature changes, urinary comfort, intimate-function changes, and anything else that feels off. Pair it with the three daily actions below. Bring the log to a menopause-informed or hormone-informed clinician.",
    this_week_male: "Start a 14-day log: morning energy, libido, sleep quality, grip strength feel, mood, recovery from workouts, and anything else that feels off. Pair it with the three daily actions below. Bring the log to your primary care doctor or a men's-health clinician and ask for testosterone, thyroid, vitamin D, and a basic metabolic panel.",
    this_week_female: "Start a 14-day symptom log: sleep, energy, mood, hot flashes or night sweats, cycle changes, urinary comfort, intimate-function changes, and anything else that feels off. Pair it with the three daily actions below. Bring the log to a menopause-informed or hormone-informed clinician.",
    daily_micro_wins: [
      "Resistance training movement today — even 10 minutes of squats, push-ups, bands, or bodyweight work.",
      "Eat a palm-sized protein at every meal to support muscle, recovery, and metabolic health.",
      "Track one symptom today: sleep hours, hot flashes, energy 1–10, mood 1–10, or intimate-function notes.",
    ],
    daily_micro_wins_male: [
      "Resistance training movement today — even 10 minutes of squats, push-ups, or bands. Strength training moves testosterone more than supplements do.",
      "Eat a palm-sized protein at every meal to support muscle, recovery, and metabolic health.",
      "Track one signal today: morning energy 1–10, libido 1–10, sleep hours, or recovery from yesterday.",
    ],
    daily_micro_wins_female: [
      "Resistance training movement today — even 10 minutes of squats, push-ups, bands, or bodyweight work. Muscle is protective through every hormone shift.",
      "Eat a palm-sized protein at every meal to support muscle, recovery, and metabolic health.",
      "Track one symptom today: sleep hours, hot flashes or night sweats, energy 1–10, mood 1–10, or cycle notes.",
    ],
    weekly_additions: [
      null,
      "Set a consistent sleep window — bedtime within 30 minutes of the same time every night.",
      "Take a 20-minute walk after one meal a day to support circulation and metabolic health.",
      "Add one strength session (2 total this week) — focus on legs, back, and grip.",
      "Reduce evening alcohol if hot flashes, night sweats, sleep, or urinary symptoms are present.",
      "Schedule a clinician conversation using the questions below as your prep.",
      "Check waist measurement and grip strength — practical markers of healthy aging.",
      "Review your supplement and medication list with a pharmacist or clinician.",
    ],
    weekly_check_in: "Days you logged symptoms and hit your strength + protein targets.",
    if_did_well: "That's exactly what a good clinician wants to see — real data, not a feeling. Next week, book the appointment. Bring the log.",
    if_did_partial: "Even 5 days of data is useful. Same target next week. Don't try to fix everything before the clinician sees the pattern.",
    if_did_zero: "No judgment. Pick one thing — write down how you slept tonight. That's the whole assignment.",
    what_youll_notice: "Week 1: you'll start to see patterns. Week 2-3: you'll have data to bring to a clinician. Week 4+: the lifestyle pieces (sleep, strength, protein) start moving the needle for most people.",
    avoid: "Online testosterone boosters with undisclosed ingredients. Compounded hormones without clinician oversight. Saliva-test-only treatment decisions. Crash diets that worsen fatigue and mood. Adjusting prescribed hormone therapy without your clinician.",
    avoid_male: "Online testosterone boosters with undisclosed ingredients. TRT clinics that prescribe without proper bloodwork. Crash diets that tank energy and libido. Adjusting prescribed hormone therapy without your clinician. Anabolic steroids — different category, real consequences.",
    avoid_female: "Compounded hormones without clinician oversight. Saliva-test-only treatment decisions. Crash diets that worsen fatigue, mood, and bone density. Adjusting prescribed hormone therapy without your clinician. Internet 'menopause cures' with proprietary blends.",
    red_flags: "Unexplained vaginal bleeding, blood in urine, breast changes, chest pain, severe shortness of breath, sudden neurologic symptoms, severe depression or self-harm thoughts, or active hormone-sensitive cancer — seek medical care directly. Do not self-coach these.",
    red_flags_male: "Chest pain, severe shortness of breath, sudden neurologic symptoms, blood in urine, testicular lumps or pain, breast tissue changes, severe depression or self-harm thoughts, or active hormone-sensitive cancer — seek medical care directly. Do not self-coach these.",
    red_flags_female: "Unexplained vaginal bleeding (especially post-menopause), blood in urine, breast changes or lumps, chest pain, severe shortness of breath, sudden neurologic symptoms, severe depression or self-harm thoughts, or active hormone-sensitive cancer — seek medical care directly. Do not self-coach these.",
    tracking_metric: "Days logged / symptoms tracked / clinician conversation booked",
    review_days: 7,
    escalation: "If you have a history of hormone-sensitive cancer, are pregnant or breastfeeding, trying to conceive, or have unexplained bleeding — speak with your clinician before starting any supplement, peptide, or hormone-adjacent therapy.",
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  CARDIOVASCULAR PROTOCOL (stage-aware, added 2026-05-15)
  //  Stages are derived from Phase 1 Q23 (cardio severity):
  //    prevention      — "I don't have any cardiovascular condition"
  //    early_warning   — controlled or uncontrolled high BP / cholesterol
  //    post_event      — AFib / heart failure / heart attack / stroke / stent / bypass
  //  Source: 2026 ACC/AHA + Gemini synthesis (peer-reviewed and reviewed by Keith).
  // ─────────────────────────────────────────────────────────────────────────
  CARDIOVASCULAR_PROTOCOL: {
    category: 'heart_fitness',
    theme: 'Protect Your Heart',
    difficulty: 'moderate',
    time_per_day: '15-30 min',
    coach_intro: "Your heart wants three things: flexible vessels, stable blood sugar, and a calm nervous system. Almost everything we'll do supports one or more of those. We meet you where you are — what you need this week depends on whether your heart's healthy, showing warning signs, or you've already had an event.",
    coach_intro_prevention: "You don't have a cardiovascular condition yet, and the goal is to keep it that way. The single biggest predictor of a future heart event is what you do in the years before one. Walking, fiber, calm breathing — that's the playbook. We'll build it together.",
    coach_intro_early_warning: "High blood pressure or cholesterol is the body's early-warning light. It is not a verdict — it's a window. The next 12 months are the most important you'll have to bend the curve. We'll work alongside your doctor, not instead of them, and focus on the things that move the needle fastest.",
    coach_intro_post_event: "You've had an event — or your heart is doing something it shouldn't. The rules change here. We're not chasing performance, we're chasing rehabilitation. Everything below is conservative, doctor-approved-style work. If your cardiologist tells you something different than I do, listen to them first.",
    this_week: "Pick one anchor habit and protect it like a meeting. Three days, same time, same place.",
    this_week_prevention: "Walk 30 minutes at a conversational pace 5 days this week. Add a 10-minute walk after one meal a day — even just dinner — to blunt the post-meal blood sugar spike that scars arteries over time.",
    this_week_early_warning: "Two anchors this week: (1) walk 30 minutes 5 days, conversational pace, and (2) eat one bowl of soluble fiber (oats, beans, or a tablespoon of psyllium in water) every day. Soluble fiber soaks up LDL cholesterol like a sponge. Keep sodium under 1,500 mg/day. Bring your latest BP + lipid numbers to your next doctor visit.",
    this_week_post_event: "Walking only this week — short and frequent. 10-15 minutes, 2-3 times a day, at a slow pace. No intensity, no intervals. Confirm with your cardiologist what's safe before you add anything else. If you're in a formal cardiac rehab program, follow that plan first.",
    daily_micro_wins: [
      'Take a 10-minute walk after your largest meal to blunt the post-meal blood sugar spike.',
      'Eat one fiber-rich food at one meal (oats, beans, berries, chia, vegetables).',
      'Box breathing — 4 seconds in, 4 hold, 4 out, 4 hold — for 2 minutes. Lowers cortisol, the inflammation driver.',
    ],
    daily_micro_wins_prevention: [
      'Walk 30 minutes at conversational pace — the kind where you can talk but not sing.',
      'Eat leafy greens or a fatty-fish meal (the nitrates and omega-3s actually dilate vessels).',
      'Box breathing 4-4-4-4 for 2-5 minutes to keep resting heart rate trending down.',
    ],
    daily_micro_wins_early_warning: [
      'Eat one bowl of oats, ½ cup of beans, or a tablespoon of psyllium in water. Soluble fiber removes LDL.',
      'Walk 30 minutes — combine 20 min Zone-2 cardio with 10 min of light resistance work this week.',
      'Keep sodium under 1,500 mg today. Read one label before you eat it.',
    ],
    daily_micro_wins_post_event: [
      'Walk 10-15 minutes at a very slow pace, 2-3 times today. No intensity.',
      'Eat one serving of berries, dark chocolate (70%+ cacao), or pomegranate to support reperfusion repair.',
      'Connect with one person today — talk, call, text. Post-event depression is real and hits adherence hard.',
    ],
    weekly_additions: [
      null,
      'Add a 10-minute post-meal walk after dinner.',
      'Add one bowl of oats or beans to your week.',
      'Try 5 minutes of box breathing twice today instead of once.',
      'Swap one processed-meat meal for a fish or legume meal.',
      'Walk for 30 minutes outside in daylight to combine cardio + vitamin D.',
      'Track your resting heart rate one morning this week.',
      'Replace one coffee with green tea (EGCG supports vessel health).',
    ],
    weekly_additions_prevention: [
      null,
      'Hit 7,000-10,000 steps a day this week. People who walk that much have markedly lower second-cardiac-event risk.',
      'Add one strength session — 20 minutes is plenty. Strong muscles pull glucose out of the bloodstream.',
      'Try 30 minutes of Zone 2 cardio — chatty pace, mostly nose-breathing.',
      'Add raw garlic or onions to one meal this week — sulfur compounds support blood thinning.',
      'Hang from a pull-up bar for 30 seconds — grip strength is one of the strongest heart-longevity predictors.',
      'Cold finish your shower: 30 seconds of cold at the end. Boosts cardiovascular reserve.',
      'Schedule a calcium-score CT scan if you are over 40 and have never had one. Best heart-age proxy out there.',
    ],
    weekly_additions_early_warning: [
      null,
      'Build to 20 minutes of Zone 2 cardio + 15 minutes of light resistance training, 3 days this week.',
      'Replace one processed meal with the "DASH plate" — half vegetables, quarter lean protein, quarter whole grain.',
      'Add magnesium glycinate (300 mg) before bed if your doctor approves — relaxes blood vessels.',
      'CoQ10 conversation with your doctor if you\'re on a statin (statins deplete CoQ10).',
      'Add one serving of fatty fish (salmon, sardines) twice this week, or take 2 g of high-quality omega-3.',
      'Track your blood pressure at home 3 mornings this week. Bring the numbers to your next appointment.',
      'Walk barefoot on grass for 5 minutes ("grounding") — early studies show it lowers inflammatory markers.',
    ],
    weekly_additions_post_event: [
      null,
      'Build to 15 minutes of supervised walking 2-3 times daily — only if your cardiologist clears it.',
      'Add one Mediterranean-style meal: olive oil, fish, greens, beans, whole grains.',
      'Confirm you have a cardiac rehab program lined up — every week without one is a week of lost recovery.',
      'Try 5 minutes of laughter therapy daily — watch something funny. Endothelial relaxation is real.',
      'Add one social call this week — connection materially improves post-event survival.',
      'Track your resting heart rate every morning this week.',
      'By week 12 with your cardiologist\'s clearance, build toward the "150-minute rule" — 30 minutes of moderate activity, 5 days a week.',
    ],
    weekly_check_in: 'Days you walked + days you hit fiber + how your resting heart rate felt this week.',
    if_did_well: 'That\'s the engine for everything else — vessel flexibility, blood sugar control, calm nervous system. Hold the new baseline two weeks, then we add a stretch goal.',
    if_did_partial: 'Walks are usually anchored — they either attach to a meal or a time of day, or they don\'t happen. Pick the anchor. Same target next week.',
    if_did_zero: 'No judgment. Pick one thing: 10 minutes of walking after dinner tonight. That\'s the whole assignment.',
    what_youll_notice: 'Week 2: easier breathing on stairs. Week 4: resting heart rate drops 3-5 bpm. Week 8+: real change in blood pressure and lipids.',
    avoid: 'Trans fats (anything fried in restaurant oil that\'s been used repeatedly). Excessive alcohol — it weakens heart muscle. Skipping prescribed meds because "you feel fine."',
    avoid_post_event: 'Any unsupervised intensity. Stimulants — even hidden ones in tea or soda — can cause arrhythmias right now. Grapefruit if you\'re on certain cardiac meds (it interferes with metabolism). High-dose antioxidant pills during active treatment without your cardiologist\'s sign-off.',
    red_flags: 'Chest pain or pressure, severe shortness of breath, sudden one-sided weakness or speech changes, fainting, or a racing/skipping heart that won\'t calm down — these are 911, not coaching.',
    red_flags_post_event: 'Any chest pain, severe shortness of breath, sudden weakness or speech changes, fainting, ankle/leg swelling that\'s new, or a heart rate that won\'t come down — call your cardiologist or go to the ER. We do not coach these.',
    tracking_metric: 'Days walked + fiber meals + resting heart rate trend',
    review_days: 7,
  },

  // ─────────────────────────────────────────────────────────────────────────
  //  CANCER PROTOCOL (stage-aware, added 2026-05-15)
  //  Stages derived from Phase 1 Q24 (cancer severity):
  //    prevention        — "I don't have and haven't had cancer"
  //    survivorship      — non-melanoma treated / in remission 5+ years
  //    during_treatment  — active treatment, recent (<5 yrs), or metastatic
  //  Source: 2026 ACS/AICR/ASCO synthesis with stage-specific overlays.
  //  IMPORTANT: This protocol explicitly is supportive care, not curative.
  // ─────────────────────────────────────────────────────────────────────────
  CANCER_PROTOCOL: {
    category: 'longevity_prevention',
    theme: 'Cancer-Resilient Living',
    difficulty: 'moderate',
    time_per_day: '15-30 min',
    coach_intro: "Cancer is a body-wide topic — metabolism, inflammation, DNA repair, microbiome, sleep, and stress all play in. No diet or supplement is a substitute for medical treatment. What we focus on is everything that supports the body around treatment, prevents recurrence, or lowers risk in the first place.",
    coach_intro_prevention: "You don't have cancer, and almost everything you can do for prevention also lowers your risk of heart disease, diabetes, and dementia at the same time. Movement, fiber, plant variety, sleep, and not smoking — that's 70% of the protective effect. We'll keep this practical.",
    coach_intro_survivorship: "You've been through it. The biggest things you can do now — and the data on this is strong — are regular movement, a Mediterranean-style plate, weight management, and addressing the lingering psychological piece (fear of recurrence is real and it impacts quality of life). For breast and colon cancer survivors specifically, regular exercise is linked to a 40-50% reduction in recurrence and all-cause mortality. That's bigger than any pill.",
    coach_intro_during_treatment: "We're not your oncology team — we're the body around the treatment. Everything we do during active treatment is conservative and supportive: maintain muscle, manage fatigue, protect immunity, support sleep, calm the nervous system. Anything that touches treatment timing or supplements goes through your oncologist first. No exceptions.",
    this_week: 'Pick one anchor habit and protect it. Same time, same place, three days.',
    this_week_prevention: 'Aim for 30 minutes of moderate activity 5 days, eat 30 grams of fiber a day (oats, beans, vegetables, berries), and cut processed meats to one serving or less this week. Those three move the dial more than any supplement.',
    this_week_survivorship: 'Walk or move 30 minutes 5 days this week, add 2 days of light strength training, and build one Mediterranean-style meal a day (olive oil, fish or beans, greens, whole grains). Check whether your vitamin D is above 30 ng/mL — if not, ask your doctor about supplementing.',
    this_week_during_treatment: 'Walk 10-20 minutes a day at a slow, gentle pace — only as your treatment day allows. Eat small, frequent, calorie-dense meals if appetite is low (bone broth, avocado, oat porridge, eggs). Pause any high-dose antioxidant supplements (Vit C, E, NAC) unless your oncologist prescribed them — they can blunt some treatments.',
    daily_micro_wins: [
      'Add one fiber-rich food to a meal (oats, beans, berries, vegetables, chia).',
      'Walk 10-30 minutes today at conversational pace.',
      'Sleep in a cool, dark room — your nightly cancer-scanning and DNA-repair window depends on it.',
    ],
    daily_micro_wins_prevention: [
      'Eat one serving of cruciferous vegetables (broccoli, cauliflower, kale) today.',
      'Walk 30-45 minutes today at conversational pace.',
      'Aim for 30 grams of fiber today — track it once just to see where you start.',
    ],
    daily_micro_wins_survivorship: [
      'Walk 30-45 minutes today at conversational pace — keep recurrence risk down.',
      'Eat a Mediterranean plate: olive oil, fish or beans, greens, whole grains.',
      'Eat one serving of high-fiber prebiotic food (onions, garlic, leeks, oats) to rebuild gut microbiome.',
    ],
    daily_micro_wins_during_treatment: [
      'Walk 10-20 minutes today at a slow pace — or just stand up and stretch every hour if a full walk is too much.',
      'Eat small frequent meals — bone broth, avocado, soft eggs, oat porridge — to fight treatment-related muscle loss.',
      'Try 5 minutes of mindfulness or slow breathing to manage chemo brain and anxiety.',
    ],
    weekly_additions: [
      null,
      'Add one cruciferous-vegetable serving daily (broccoli, kale, cauliflower).',
      'Add 30 seconds of cold water at the end of one shower — boosts natural killer (NK) cells.',
      'Replace one coffee with green tea (EGCG supports cancer-cell apoptosis).',
      'Add one cup of berries or one square of >85% dark chocolate as your sweet — anthocyanins.',
      'Take 1-2 Brazil nuts daily (selenium, thyroid + antioxidant enzymes).',
      'Add a 12-hour eating window 3 nights this week (no food for 12 hrs overnight) — autophagy.',
      'Try 5 minutes of forest bathing / nature time to lower cortisol-induced mutations.',
    ],
    weekly_additions_prevention: [
      null,
      'Cut processed meats (bacon, deli, sausage) to one serving or fewer this week — Class 1 carcinogens.',
      'Add 2 sessions of HIIT or heavy resistance training — mitochondrial health is your DNA\'s defense system.',
      'Add omega-3s — 2 g of high-quality fish/algae oil daily, or 2 fatty-fish meals.',
      'Eat lycopene-rich cooked tomatoes (sauce, paste) twice this week — prostate/breast protection.',
      'Replace seed oils with olive or avocado oil for cooking — lowers oxidative stress.',
      'Add one serving of fermented food (kimchi, sauerkraut, kefir) — gut microbiome immune training.',
      'Schedule a Galleri / multi-cancer liquid biopsy if you\'re over 50 or have a family history.',
    ],
    weekly_additions_survivorship: [
      null,
      'Build to 150 minutes of moderate cardio + 2 strength sessions per week (the evidence-based survivorship target).',
      'Add one fermented food daily — rebuild the microbiome chemo or treatment knocked down.',
      'Add a prebiotic (onions, garlic, leeks, oats) to one meal daily.',
      'Cut highly processed "ultra-palatable" foods — they spike IGF-1, linked to recurrence.',
      'Check vitamin D — aim for above 30 ng/mL (50+ ideal). Ask your doctor before supplementing.',
      'Join or continue a survivor support group — addresses fear of recurrence, the #1 quality-of-life driver post-treatment.',
      'Add eccentric strength work (slow-lowering squats and push-ups) — triggers myokines that signal repair.',
    ],
    weekly_additions_during_treatment: [
      null,
      'Eat protein at every meal — 1.2-1.5 g per kg body weight to prevent muscle wasting.',
      'Try restorative yoga or 10-min slow walk after rest periods to prevent blood clots.',
      'Add ginger tea for nausea (clinical support is moderate but real).',
      'Avoid raw sprouts, unpasteurized juices, raw sushi, and grapefruit — infection risk and drug interactions.',
      'Build a "chemo brain" 5-minute cognitive routine: a puzzle, a memory game, or a short reading.',
      'Confirm with your oncologist before adding any supplement, even vitamin D.',
      'One mindfulness or breathing session daily to manage diagnostic anxiety.',
    ],
    weekly_check_in: 'Days you walked + fiber meals + how your energy felt this week.',
    if_did_well: 'That\'s how the body wants to be supported — movement, fiber, real food, real sleep. Hold the baseline two weeks, then we add one stretch goal.',
    if_did_partial: 'Pick the smaller anchor next week — five-minute walks instead of 30, one fiber food at breakfast instead of three meals. Consistency beats volume.',
    if_did_zero: 'No judgment. One thing — drink a glass of water with breakfast tomorrow. That\'s the whole week.',
    what_youll_notice: 'Week 2-3: easier digestion, steadier afternoon energy. Week 4-8: real metabolic shifts. Year 1+: this is the kind of work that compounds quietly.',
    avoid: 'Processed meats. Trans fats. Excessive alcohol. High-dose isolated antioxidant pills during active treatment unless prescribed.',
    avoid_prevention: 'Processed meats (Class 1 carcinogens). Trans fats. Daily alcohol — any amount raises breast, colon, and esophageal cancer risk. High-dose Vitamin E + Selenium pills (the SELECT trial showed elevated prostate cancer risk).',
    avoid_survivorship: 'Highly processed "ultra-palatable" foods (chips, sugary cereals) — they spike IGF-1, linked to recurrence. Tobacco of any kind. Daily alcohol. Skipping follow-up screening to "move on."',
    avoid_during_treatment: 'High-dose antioxidant pills (Vit C, E, NAC) without oncologist sign-off — they can protect cancer cells from oxidative-stress treatments. Grapefruit (interferes with metabolism of many chemo drugs). Raw sushi, raw sprouts, unpasteurized juice — your immune system is compromised. Any new supplement without telling your oncologist.',
    red_flags: 'Unexplained weight loss, persistent fever, new lumps, blood in stool or urine, persistent pain in one location for more than 3 weeks, unusual fatigue that does not respond to sleep — get evaluated. We do not coach these.',
    red_flags_during_treatment: 'Fever above 100.4°F, uncontrolled vomiting, persistent diarrhea, signs of infection at IV sites, severe shortness of breath, severe abdominal pain, neurologic changes, or thoughts of self-harm — call your oncology team immediately. Do not self-coach these.',
    tracking_metric: 'Days with movement + fiber + sleep hours',
    review_days: 7,
    escalation: 'If you have an active hormone-sensitive cancer, are immunocompromised, or recently started a new chemotherapy regimen, speak with your oncologist before starting ANY supplement or major dietary shift.',
  },

  DIGESTIVE_PROTOCOL: {
    category: 'digestive_microbiome',
    theme: 'Support Your Gut',
    difficulty: "easy",
    time_per_day: "5 min",
    coach_intro: "Your gut likes rhythm. Same meals around the same times, real fiber, real water, and time to actually digest before bed. We add slowly so we can tell what's helping and what isn't — your gut doesn't reward overhauls.",
    this_week: "Add one fiber food at breakfast — oats, berries, chia, beans, or a tablespoon of psyllium in water. Drink water with it. Stop eating 2-3 hours before bed. That's the week.",
    daily_micro_wins: [
      "Add one fiber-rich food to a meal (oats, beans, berries, chia, flax, vegetables).",
      "Drink a full glass of water with each meal.",
      "Eat slowly — chew well, no screens at the table.",
    ],
    weekly_additions: [
      null,
      "Stop eating 2-3 hours before bedtime to reduce reflux risk.",
      "Add one new plant food to your week (different beans, vegetable, or grain).",
      "Try one fermented food (yogurt, kefir, sauerkraut, kimchi) if it agrees with you.",
      "Track digestive symptoms for 3 days — what you ate, what you felt.",
      "Walk for 10 minutes after one meal a day.",
      "Add psyllium with a full glass of water if constipation persists.",
      "Try a 5-minute breathing practice before your largest meal.",
    ],
    weekly_check_in: "Days with fiber, water with meals, and how your symptoms felt 1–10.",
    if_did_well: "That's how the gut wants to be treated — slow, rhythmic, generous with plants. Next week we add diversity. Different beans, different fibers, different colors.",
    if_did_partial: "Fiber takes time. Same target — and add water with it. If you're getting more bloating, you ramped too fast. Slow down, don't quit.",
    if_did_zero: "No judgment. The win this week is one piece of fruit at breakfast. That's it.",
    what_youll_notice: "Week 1: more regular. Week 2-3: less bloating after meals. Week 4+: real differences in energy and digestion.",
    avoid: "Random probiotic stacks. Aggressive cleanses or colon detoxes. Ramping fiber too fast without water. Self-coaching red-flag symptoms.",
    red_flags: "Blood in stool, black stools, unexplained weight loss, trouble swallowing, persistent vomiting, severe abdominal pain, persistent diarrhea, or a major change in bowel habits — get medical care. These are not coached, they are evaluated.",
    tracking_metric: "Days with fiber + water at meals / symptom score 1–10",
    review_days: 7,
  },

}

// Re-export under the old name for backward compat with the existing
// renderer in Phase2Results.jsx and CoachDashboard.jsx.
export const PROTOCOL_LIBRARY = COACHING_PROTOCOLS

/**
 * Resolve a protocol's content for a specific user context (sex + stage).
 *
 * Sex variants (`${field}_male` / `${field}_female`) — hormone health and
 * other body-system protocols read genuinely differently for men vs. women.
 *
 * Stage variants (`${field}_prevention` / `${field}_early_warning` /
 * `${field}_during_treatment` / `${field}_survivorship` / `${field}_post_event`) —
 * cancer and cardiovascular protocols change radically based on where the
 * user is in the disease arc. We derive stage from Q23 (cardio severity)
 * and Q24 (cancer severity), so no extra question is required.
 *
 * Stage wins over sex when both are present (a survivor's safety guidance
 * is identical regardless of sex; sex copy may still apply for non-staged
 * fields). Caller can pass `{ sex, stage }`. Either is optional. Unknown
 * values fall through to the unisex / unstaged copy.
 */
const VARIANT_AWARE_FIELDS = [
  'coach_intro',
  'this_week',
  'daily_micro_wins',
  'weekly_additions',
  'weekly_check_in',
  'if_did_well',
  'if_did_partial',
  'if_did_zero',
  'what_youll_notice',
  'avoid',
  'red_flags',
  'escalation',
  'theme',
]

const VALID_SEXES = new Set(['male', 'female'])
const VALID_STAGES = new Set([
  'prevention',
  'early_warning',
  'during_treatment',
  'survivorship',
  'post_event',
])

export function resolveProtocolBySex(content, sex) {
  return resolveProtocol(content, { sex })
}

export function resolveProtocol(content, { sex, stage } = {}) {
  if (!content) return content
  const out = { ...content }
  const sexKey = VALID_SEXES.has(sex) ? sex : null
  const stageKey = VALID_STAGES.has(stage) ? stage : null
  for (const field of VARIANT_AWARE_FIELDS) {
    // Stage wins over sex — cancer/CVD stage guidance is the higher-stakes
    // signal. If both happen to be authored for the same field, stage takes it.
    if (stageKey && Object.prototype.hasOwnProperty.call(content, `${field}_${stageKey}`)) {
      out[field] = content[`${field}_${stageKey}`]
      continue
    }
    if (sexKey && Object.prototype.hasOwnProperty.call(content, `${field}_${sexKey}`)) {
      out[field] = content[`${field}_${sexKey}`]
    }
  }
  return out
}

/**
 * Map the user's Q23 (cardio severity) answer text to a stage label used by
 * resolveProtocol. Q23 is a single-select with `years` baked in; we read the
 * text (or value) rather than re-deriving from years to stay robust.
 */
export function cardioStageFromAnswer(answerText) {
  if (!answerText) return null
  const t = String(answerText).toLowerCase()
  if (t.includes("don't have") || t.includes("dont have")) return 'prevention'
  if (t.includes('heart attack') || t.includes('stroke') || t.includes('stent') || t.includes('bypass')) return 'post_event'
  if (t.includes('afib') || t.includes('atrial fibrillation') || t.includes('heart failure')) return 'post_event'
  if (t.includes('high blood pressure') || t.includes('high cholesterol')) return 'early_warning'
  return null
}

/**
 * Map the user's Q24 (cancer severity) answer text to a stage label.
 * "Don't have" → prevention. "In remission 5+ years" / non-melanoma treated
 * → survivorship. "Active treatment" / metastatic → during_treatment.
 */
export function cancerStageFromAnswer(answerText) {
  if (!answerText) return null
  const t = String(answerText).toLowerCase()
  if (t.includes("don't have") || t.includes("haven't had") || t.includes("havent had")) return 'prevention'
  if (t.includes('metastatic') || t.includes('stage iv') || t.includes('active treatment') || t.includes('recent')) return 'during_treatment'
  if (t.includes('remission') || t.includes('non-melanoma') || t.includes('treated')) return 'survivorship'
  return null
}

/**
 * Convenience: derive the right stage for a given protocol category from a
 * user's phase 1 answers payload. Returns null for categories that don't
 * support stage variants.
 */
export function stageForCategoryFromAnswers(categoryId, phase1Answers) {
  if (!phase1Answers) return null
  if (categoryId === 'heart_fitness' || categoryId === 'longevity_prevention') {
    // Cardio stage applies cleanly to heart_fitness. For longevity_prevention
    // we lean cardio first since CVD is the dominant longevity risk.
    return cardioStageFromAnswer(phase1Answers?.[23]?.text)
  }
  // Cancer protocol category not directly modeled in PHASE2_CATEGORIES today;
  // if it gets added, this is where we'd map it. Hook ready.
  return null
}
